// pages/BusinessCardDetailPage.tsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout, Typography, Button, Card, Tabs, Descriptions, Space, Tag, Form, Input, Row, Col, Modal, List, Avatar, Divider, Select, Spin, Upload, Progress, message, Dropdown } from 'antd';
import { ArrowLeftOutlined, EditOutlined, PhoneOutlined, MailOutlined, HomeOutlined, LinkOutlined, UserOutlined, SaveOutlined, PlusOutlined, FileTextOutlined, MessageOutlined, TagOutlined, RobotOutlined, SoundOutlined, UploadOutlined, PaperClipOutlined, CloseOutlined, ZoomInOutlined, DeleteOutlined, CloseCircleFilled, EyeOutlined, DownloadOutlined, DownOutlined, UpOutlined, SendOutlined, HistoryOutlined, ShareAltOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { getRoutePath, API_BASE_PATH } from '../config';
import { fetchWithTokenRefresh } from '../services/token-manager';
import CustomerSelectPanel from '../components/CustomerSelectPanel';
import { businessCardAPI, getCurrentDetailCustomerId } from '../services/api';
import { Customer } from '../types/businessCard';
import { getRankColorSync, saveCustomerRank, getRankColorByIndex } from '../services/rank-utils';
import { useRankColors } from '../hooks/useRankColors';
import { useRankOptions } from '../hooks/useRankOptions';
import { indexedDBStorage } from '../services/storage/indexedDB';
import { followAPI, buildNoticeDestinationList, buildInitialNotifRecipients, toFollowComment } from '../services/follow-api';
import { additionalAPI, ContactUser } from '../services/additional-api';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';
import axios from 'axios';
import { AssistInputState, SelectEnqueteInputAidModal } from '../services/assist-input';
import { optionFieldAPI, OptionFieldListResponse } from '../services/option-field-api';
import OptionFieldGroup from '../components/OptionFields/OptionFieldGroup';
import { OptionFieldValidator } from '../utils/optionFieldValidation';
import { NotifRecipientWithDetails, FollowComment } from '../types/follow';
import NotifRecipientModal, { AlertSettings } from '../components/NotifRecipientModal';
import FollowFormModal, { FollowFormValues, OnsiteCustomer } from '../components/FollowFormModal';
import { useRelatedFollowActions, RelatedFollowAnchor } from '../hooks/useRelatedFollowActions';
import { useFollowComments } from '../hooks/useFollowComments';
import './BusinessCardDetailPage.css';
import { Linkify, openUrl } from '../utils/linkify';
import { isInFlutter } from '../utils/flutterDetection';
import { useFollowFormFeatures } from '../hooks/useFollowFormFeatures';
import { previewAttachFile, downloadAttachFile } from '../utils/fileOperations';
import { AutoKanaHandler, hiraganaToKatakana, isHiraganaOnly } from '../utils/autoKana';
import { useContactUsers } from '../hooks/useContactUsers';
import { hasAuth, AUTH, isContactUserOf } from '../utils/auth';
import FollowHistoryPanel, { FollowHistoryItem, FollowHistoryAttachment } from '../components/FollowHistoryPanel';
import FollowAttachmentModal, { FollowAttachFile } from '../components/FollowAttachmentModal';
import { convertFollowResponseToFormValues, buildAlertSettingsFromFollow } from '../utils/followDataConverter';
import { isoUtcToJstDate } from '../utils/dateUtils';
import BusinessCardImageModal from '../components/BusinessCardImageModal';
import KeywordSelectModal from '../components/KeywordSelectModal';
import AssistInputModal from '../components/AssistInputModal';
import { FORCE_API_ERROR } from '../utils/debugFlags';

// 型定義の競合を避けるため、直接Windowインターフェースは拡張しない



const { Header, Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;




interface BusinessCardDetail {
  id: number;
  name: string;
  kana?: string;
  company: string;
  department?: string;
  position?: string;
  rank: string;
  rankno?: number;  // rankno（ランク番号）を追加
  email?: string;
  phone?: string;
  mobile?: string;
  fax?: string;
  address?: string;
  url?: string;
  lastContact?: string;
  remarks?: string;
  representative?: string;
  country?: string;
  doNotDistribute?: boolean;
  // 拡張フィールド（JSON文字列）
  optionfield?: string;
  // 名刺画像
  businesscardfrontimage?: string;
  businesscardbackimage?: string;
  // 担当者番号（配列、JSON文字列配列"[13,17]"、または単一の値）
  contactuserno?: any[] | string | number;
  // フォロー履歴用
  followHistory?: FollowHistoryItem[];
}

/**
 * フォロー履歴の担当者表示名を決める。担当者が sys（userno が負）なら「システム管理者」、それ以外は渡した名前。
 */
const followRepresentative = (contactuserno: unknown, name: string): string =>
  (typeof contactuserno === 'number' && contactuserno < 0) ? 'システム管理者' : name;

/**
 * 自フォロー判定（isFollowOwner）に渡すフィールドを生値のまま抽出する。
 */
const followOwnerFields = (follow: any): Pick<FollowHistoryItem, 'contactuserno' | 'createuserno'> => ({
  contactuserno: follow.contactuserno,
  createuserno: follow.createuserno,
});

// APIレスポンスから表示用のデータモデルに変換する関数
const convertApiResponseToBusinessCardDetail = (data: any): BusinessCardDetail => {
  // APIレスポンスがnullまたはundefinedの場合、デフォルト値を返す
  if (!data || !data.customerdata) {
    return {
      id: 0,
      name: '取得できませんでした',
      company: '',
      rank: ''
    };
  }

  const customer = data.customerdata;
  
  // 一覧画面と同じく、rankname を優先的に使用する
  let rankValue = customer.rankname || '';

  return {
    id: customer.customerno || 0,
    name: customer.customername || '',
    kana: customer.customernamefurigana || '',
    company: customer.companyname || '',
    department: customer.deptname || '',
    position: customer.position || '',
    // 抽出したランク値を使用
    rank: rankValue,
    // rankno（ランク番号）も保存
    rankno: customer.rankno !== undefined ? customer.rankno : -1,
    email: customer.mailaddress || '',
    phone: customer.tel || '',
    mobile: customer.mobiletel || '',
    fax: customer.fax || '',
    address: customer.address || '',
    url: customer.url || '',
    lastContact: customer.lastcontactdate || '',
    remarks: customer.remarks || '',
    representative: customer.contactusername || '',
    country: customer.countryname || '',
    doNotDistribute: customer.maildeliveryflag === false,
    // 拡張フィールド（JSON文字列）
    optionfield: customer.optionfield || '',
    businesscardfrontimage: customer.businesscardfrontimage || '',
    businesscardbackimage: customer.businesscardbackimage || '',
    // 担当者番号の配列を保存
    contactuserno: customer.contactuserno || [],
    // フォロー履歴はAPIから取得した場合はここで変換
    followHistory: Array.isArray(customer.followhistory)
      ? customer.followhistory.map((follow: any) => ({
          id: follow.followno || 0,
          date: follow.followdate || '',
          content: follow.remarks || '',
          // 担当者が sys（userno が負）の場合は「システム管理者」と表示
          representative: followRepresentative(follow.contactuserno, follow.contactusername || ''),
          // 自フォロー判定用フィールド（生値のまま）
          ...followOwnerFields(follow),
          provisional: !!follow.temporaryflag || !!follow.temporary_flag || follow.provisional === 1,
          plantodo: !!follow.plantodoflag,
        }))
      : []
  };
};

const BusinessCardDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const { user } = useAuth(); // AuthContextからユーザー情報を取得

  // フォロー編集/削除の可否は FollowHistoryPanel 内で canEditFollow / canDeleteFollow に集約
  const canEditContactUser = hasAuth(user, AUTH.FC_EDIT_CONTACTUSER);
  // 顧客登録/編集の画面権限（名刺取込ボタンの表示と、顧客編集可否のベース条件に使用）
  const canRegisterCustomer = hasAuth(user, AUTH.SC_CUSTOMER_EDIT);

  const [activeTab, setActiveTab] = useState<string>('1');
  const [cardData, setCardData] = useState<BusinessCardDetail | null>(null);

  // 顧客編集権限 = 画面権限(SC_CUSTOMER_EDIT) かつ (全件編集権限 or 自担当)
  const canEdit = canRegisterCustomer && (hasAuth(user, AUTH.FC_EDIT_ALL_CUSTOMER) || isContactUserOf(cardData?.contactuserno, user?.userno));
  // 顧客削除権限 = 削除権限あり または 自担当
  const canDeleteCustomer = hasAuth(user, AUTH.FC_DELETE_CUSTOMER) || isContactUserOf(cardData?.contactuserno, user?.userno);

  // Flutterフッター高さの管理
  const [flutterFooterHeight, setFlutterFooterHeight] = useState<number>(0);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showBackSide, setShowBackSide] = useState<boolean>(false);
  const [frontPreviewOpen, setFrontPreviewOpen] = useState<boolean>(false);
  const [backPreviewOpen, setBackPreviewOpen] = useState<boolean>(false);


  const [form] = Form.useForm();
  
  // ランク色を管理するための状態
  const [currentRankColor, setCurrentRankColor] = useState<string>('#bfbfbf');
  const [rankColors, setRankColors] = useRankColors();
  const { rankOptions } = useRankOptions({ fallbackLabels: 'japanese' });
  const [showAddFollowModal, setShowAddFollowModal] = useState<boolean>(false);
  const [followNotifModalVisible, setFollowNotifModalVisible] = useState<boolean>(false);
  // フォロー登録通知モーダルの初期選択受信者
  const [followNotifInitialRecipients, setFollowNotifInitialRecipients] = useState<NotifRecipientWithDetails[]>([]);
  // フォロー登録通知モーダルでアラート設定セクションを表示するか（フォーム送信時の isTodo を保持）
  const [followNotifIsTodo, setFollowNotifIsTodo] = useState<boolean>(false);
  // TODO: Teams通知実装時はコメントアウトを解除（フォロー登録モーダル用 Teamsチャネル一覧）
  // const [followNotifTeamsChannels, setFollowNotifTeamsChannels] = useState<{ url: string; name: string }[]>([]);
  // フォロー履歴一覧から直接通知を送る用
  const [historyNotifModalVisible, setHistoryNotifModalVisible] = useState<boolean>(false);
  const [historyNotifFollowNo, setHistoryNotifFollowNo] = useState<number | null>(null);
  // フォロー履歴通知モーダルの初期選択受信者
  const [historyNotifInitialRecipients, setHistoryNotifInitialRecipients] = useState<NotifRecipientWithDetails[]>([]);
  // フォロー履歴通知モーダル: 対象フォローが ToDo かどうか（アラート設定 UI の表示制御）
  const [historyNotifIsTodo, setHistoryNotifIsTodo] = useState<boolean>(false);
  // フォロー履歴通知モーダル: アラート設定の初期値（DB 値）
  const [historyNotifAlertSettings, setHistoryNotifAlertSettings] = useState<Partial<AlertSettings> | undefined>(undefined);
  // TODO: Teams通知実装時はコメントアウトを解除（履歴通知モーダル用 Teamsチャネル一覧）
  // const [historyNotifTeamsChannels, setHistoryNotifTeamsChannels] = useState<{ url: string; name: string; defaultFlag?: boolean }[]>([]);
  // フォロー通知履歴モーダル
  const [followNotifHistoryModalVisible, setFollowNotifHistoryModalVisible] = useState<boolean>(false);
  const [followNotifHistoryItems, setFollowNotifHistoryItems] = useState<any[]>([]);
  const [followNotifHistoryLoading, setFollowNotifHistoryLoading] = useState<boolean>(false);
  // フォローフォームの初期値（FollowFormModal に渡す）
  const [followFormInitialValues, setFollowFormInitialValues] = useState<Partial<FollowFormValues> | undefined>(undefined);
  // フォローモーダルのモード（新規/編集）
  const [followModalMode, setFollowModalMode] = useState<'new' | 'edit'>('new');
  // 編集時のアラート設定初期値（DB から取得した値を NotifRecipientModal に渡す）
  const [followEditAlertSettings, setFollowEditAlertSettings] = useState<Partial<AlertSettings> | undefined>(undefined);
  // フォローフォーム送信値を一時保持（通知モーダル経由での保存に使用）
  const pendingFollowValuesRef = useRef<FollowFormValues | null>(null);
  const followHistoryTabRef = useRef<HTMLDivElement | null>(null);
  const [fileList, setFileList] = useState<any[]>([]);
  const [attachFiles, setAttachFiles] = useState<File[]>([]);
  const [deleteFileConfirmVisible, setDeleteFileConfirmVisible] = useState(false);
  const [deleteFileTargetIndex, setDeleteFileTargetIndex] = useState<number | null>(null);
  const [historyAttachModalVisible, setHistoryAttachModalVisible] = useState(false);
  const [historyAttachModalFiles, setHistoryAttachModalFiles] = useState<FollowAttachFile[]>([]);
  const [historyAttachModalFollowIndex, setHistoryAttachModalFollowIndex] = useState<number | null>(null);
  const [processingModalVisible, setProcessingModalVisible] = useState(false);
  const [followNo, setFollowNo] = useState<number | null>(null);
  // 編集中フォローの関連フォロー付随情報（所属群No・紐づけ検索シード）。
  // followNo は他機能（添付・通知）でも使う中核 state のため、anchor はこれを軸に導出する。
  const [followAnchorMeta, setFollowAnchorMeta] = useState<{ groupNo: number | null; companyName?: string; customerName?: string } | null>(null);
  // フォロー編集ボタン連打時、後着応答が先着の state を上書きしないためのガード
  const latestEditRef = useRef<number | null>(null);
  const [isLoadingFollowHistory, setIsLoadingFollowHistory] = useState<boolean>(false);
  const [deleteFollowModalVisible, setDeleteFollowModalVisible] = useState<boolean>(false);
  const [deleteFollowTargetId, setDeleteFollowTargetId] = useState<number | null>(null);
  const { contactUsers, isLoading: isLoadingContactUsers } = useContactUsers();
  // 関連フォロー（表示・紐づけ・切り離し）の共有ロジック。
  // anchor は followNo（フォロー番号の唯一の真実）＋ followAnchorMeta（群No・シード）から導出する。
  const followAnchor: RelatedFollowAnchor | null = followNo != null
    ? {
        followNo,
        groupNo: followAnchorMeta?.groupNo ?? null,
        companyName: followAnchorMeta?.companyName,
        customerName: followAnchorMeta?.customerName,
      }
    : null;
  const relatedFollow = useRelatedFollowActions({
    anchor: followAnchor,
    contactUsers,
    relatedZIndex: 1010,
    linkZIndex: 1020,
  });
  // 編集中フォローのコメント一覧（handleEditFollowItem で selectFollow 結果から設定。安定参照を保つため
  // followEditComments という別 state に保持し、followComments の同期 effect が毎レンダー発火しないようにする）
  const [followEditComments, setFollowEditComments] = useState<FollowComment[]>([]);
  const followComments = useFollowComments({
    followno: followNo,
    contactUsers,
    initialComments: followEditComments,
    zIndex: 1010,
  });
  // 複数の担当者を選択できるように配列に変更
  const [selectedContactUsers, setSelectedContactUsers] = useState<number[]>([]);
  const [keywordModalVisible, setKeywordModalVisible] = useState<boolean>(false);
  const [formValidity, setFormValidity] = useState<{[key: string]: {valid: boolean, message?: string}}>({});
  const [fileAddConfirmVisible, setFileAddConfirmVisible] = useState<boolean>(false);
  const [assistModalVisible, setAssistModalVisible] = useState<boolean>(false);
  const [assistOptions, setAssistOptions] = useState<Array<{enqNo: number; enqName: string}>>([]);
  const [selectedAssistEnq, setSelectedAssistEnq] = useState<number | null>(null);
  const [assistModalError, setAssistModalError] = useState<string>('');
  const [defaultCustomerAssistNo, setDefaultCustomerAssistNo] = useState<number | null>(null);
  const [customerDeleteConfirmVisible, setCustomerDeleteConfirmVisible] = useState<boolean>(false);
  const [assistContentHtml, setAssistContentHtml] = useState<string>('');
  const [loadingAssistContent, setLoadingAssistContent] = useState<boolean>(false);
  const assistContentRef = useRef<HTMLDivElement>(null);
  const assistModalErrorRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<any>(null);

  // キーワードオプション
  const [keywordOptions, setKeywordOptions] = useState<any[]>([]);
  const [isLoadingKeywords, setIsLoadingKeywords] = useState<boolean>(false);
  const [keywordLoadError, setKeywordLoadError] = useState<boolean>(false);
  const [assistOptionsError, setAssistOptionsError] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // APIエラーメッセージ表示用の状態
  const [apiError, setApiError] = useState<string | null>(null);
  
  // 拡張フィールドの状態
  const [optionFields, setOptionFields] = useState<OptionFieldListResponse | null>(null);
  const [optionFieldValues, setOptionFieldValues] = useState<Record<string, any>>({});
  const [loadingOptionFieldsData, setLoadingOptionFieldsData] = useState<boolean>(false);

  // フリガナフィールドの状態
  const [furiganaField, setFuriganaField] = useState<any | null>(null);
  const [furiganaValue, setFuriganaValue] = useState<string>('');

  // 自動カタカナ変換ハンドラ（顧客名フィールド用）
  const autoKanaHandlerRef = useRef<AutoKanaHandler>(new AutoKanaHandler());

  // 訪問/来社 同席者（客先）— CustomerSelectPanel と FollowFormModal の橋渡し
  const [customerSelectVisible, setCustomerSelectVisible] = useState<boolean>(false);
  const [onsiteCustomers, setOnsiteCustomers] = useState<OnsiteCustomer[]>([]);

  // Flutterからのフッター高さ通知を受け取る
  useEffect(() => {
    const handleFlutterFooterStateChange = (event: any) => {
      const footerHeight = event.detail?.footerHeight || 0;
      console.log('Flutter footer height received:', footerHeight);
      setFlutterFooterHeight(footerHeight);
    };

    // 初期値をチェック（Flutterから既に設定されている可能性がある）
    if (window.flutterFooterState?.footerHeight) {
      setFlutterFooterHeight(window.flutterFooterState.footerHeight);
    }

    // イベントリスナーを追加
    window.addEventListener('flutterFooterStateChange', handleFlutterFooterStateChange);

    // クリーンアップ関数
    return () => {
      window.removeEventListener('flutterFooterStateChange', handleFlutterFooterStateChange);
    };
  }, []);

  // flutterFooterHeightが確定したらCSS変数に反映（モーダルCSSで使用）
  useEffect(() => {
    document.documentElement.style.setProperty('--flutter-footer-height', `${flutterFooterHeight}px`);
  }, [flutterFooterHeight]);

  // モーダル表示時に背景スクロールを無効化
  useEffect(() => {
    let scrollY = 0;

    if (assistModalVisible || keywordModalVisible || showAddFollowModal || historyAttachModalVisible) {
      // 現在のスクロール位置を保存
      scrollY = window.scrollY;

      // モーダル表示時：背景スクロールを無効化
      document.body.classList.add('modal-open');
      document.body.style.top = `-${scrollY}px`;

      // 各モーダル用の特別なクラスを追加
      if (assistModalVisible) {
        document.body.classList.add('assist-modal-open');
        document.body.classList.remove('keyword-modal-open');
        document.body.classList.remove('voice-confirm-modal-open');
        // アシスト入力モーダルではタッチアクションの制限を緩める
        if (window.innerWidth <= 767) {
          document.documentElement.style.overflow = 'hidden';
          // touch-actionはアシスト入力モーダル内でのスクロールを許可するため設定しない
        }
      } else {
        document.body.classList.remove('assist-modal-open');
        if (keywordModalVisible) {
          document.body.classList.add('keyword-modal-open');
        } else {
          document.body.classList.remove('keyword-modal-open');
        }
        document.body.classList.remove('voice-confirm-modal-open');
        // フォロー追加モーダルなど他のモーダルでは完全に制限
        if (window.innerWidth <= 767) {
          document.documentElement.style.overflow = 'hidden';
          document.body.style.touchAction = 'none';
        }
      }
    } else {
      // モーダル非表示時：背景スクロールを復元
      const currentScrollY = document.body.style.top;

      document.body.classList.remove('modal-open');
      document.body.classList.remove('assist-modal-open');
      document.body.classList.remove('keyword-modal-open');
      document.body.classList.remove('voice-confirm-modal-open');
      document.body.style.top = '';
      document.documentElement.style.overflow = '';
      document.body.style.touchAction = '';

      // スクロール位置を復元
      if (currentScrollY) {
        const scrollPosition = parseInt(currentScrollY.replace('px', '')) * -1;
        window.scrollTo(0, scrollPosition);
      }
    }

    // クリーンアップ関数
    return () => {
      document.body.classList.remove('modal-open');
      document.body.classList.remove('assist-modal-open');
      document.body.classList.remove('keyword-modal-open');
      document.body.classList.remove('voice-confirm-modal-open');
      document.body.style.top = '';
      document.documentElement.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [assistModalVisible, keywordModalVisible, showAddFollowModal, historyAttachModalVisible]);

  // フォロー追加モーダルのモバイルキーボード対応
  useEffect(() => {
    if (!showAddFollowModal) return;

    // モバイル端末でない場合は処理をスキップ
    if (window.innerWidth > 767) return;

    let isKeyboardVisible = false;
    let initialViewportHeight = window.visualViewport?.height || window.innerHeight;
    
    // Visual Viewport APIが利用できる場合の処理
    const handleVisualViewportChange = () => {
      if (!window.visualViewport) return;
      
      const currentHeight = window.visualViewport.height;
      const heightDiff = initialViewportHeight - currentHeight;
      
      // キーボードの表示/非表示を判定（100px以上の差があればキーボード表示と判定）
      const keyboardVisible = heightDiff > 100;
      
      if (keyboardVisible !== isKeyboardVisible) {
        isKeyboardVisible = keyboardVisible;
        
        // モーダルのスクロール位置を調整
        const modal = document.querySelector('.ant-modal-body') as HTMLElement;
        const activeElement = document.activeElement as HTMLElement;
        
        if (modal && activeElement && keyboardVisible) {
          // フォーカスされた要素がモーダル内の入力フィールドの場合
          if (modal.contains(activeElement) && 
              (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
            
            setTimeout(() => {
              // フォーカスされた要素の位置を計算
              const elementRect = activeElement.getBoundingClientRect();
              const modalRect = modal.getBoundingClientRect();
              
              // 要素がモーダル内で見える位置にスクロール
              const targetScrollTop = modal.scrollTop + elementRect.top - modalRect.top - 20;
              modal.scrollTo({
                top: Math.max(0, targetScrollTop),
                behavior: 'smooth'
              });
            }, 150);
          }
        }
      }
    };

    // フォールバック：resize イベントでの検知
    const handleResize = () => {
      if (window.visualViewport) return; // Visual Viewport APIが利用できる場合はスキップ
      
      const currentHeight = window.innerHeight;
      const heightDiff = initialViewportHeight - currentHeight;
      const keyboardVisible = heightDiff > 100;
      
      if (keyboardVisible !== isKeyboardVisible) {
        isKeyboardVisible = keyboardVisible;
        
        if (keyboardVisible) {
          // キーボード表示時の処理
          const activeElement = document.activeElement as HTMLElement;
          const modal = document.querySelector('.ant-modal-body') as HTMLElement;
          
          if (modal && activeElement && modal.contains(activeElement)) {
            setTimeout(() => {
              activeElement.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'nearest' 
              });
            }, 300);
          }
        }
      }
    };

    // 入力フィールドフォーカス時の処理
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        const modal = document.querySelector('.ant-modal-body') as HTMLElement;
        
        if (modal && modal.contains(target)) {
          // iOSでのキーボード表示を待ってからスクロール調整
          setTimeout(() => {
            if (window.visualViewport) {
              // Visual Viewport APIが利用できる場合は処理を任せる
              return;
            }
            
            // フォールバック処理
            const targetRect = target.getBoundingClientRect();
            const modalRect = modal.getBoundingClientRect();
            
            if (targetRect.bottom > modalRect.bottom - 50) {
              const scrollAmount = targetRect.bottom - modalRect.bottom + 70;
              modal.scrollBy({
                top: scrollAmount,
                behavior: 'smooth'
              });
            }
          }, 600); // iOSキーボードアニメーション完了を待つ
        }
      }
    };

    // イベントリスナーを追加
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
    } else {
      window.addEventListener('resize', handleResize);
    }
    
    document.addEventListener('focusin', handleFocusIn);

    // クリーンアップ関数
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
      } else {
        window.removeEventListener('resize', handleResize);
      }
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [showAddFollowModal]);
  
  // 顧客データをサーバーから取得する関数
  const fetchData = async () => {
    console.log('BusinessCardDetailPage - fetchData開始');
    try {
      const id = await getCustomerId();
      console.log('BusinessCardDetailPage - 顧客ID:', id);
      if (id === 0) {
        console.log('BusinessCardDetailPage - 顧客IDが0のため処理終了');
        return; // エラーの場合は早期リターン
      }
      
      // ローディング状態を設定
      setCardData(null);
      
      // 顧客詳細情報を取得
      // 閲覧日を更新するため第2引数でtrueセット
      const response = await businessCardAPI.getCustomerDetail(id, true);
      console.log('BusinessCardDetailPage - API レスポンス:', response);

      if (response.statuscode === 200) {
        // APIレスポンスの詳細をデバッグ
        
        // APIレスポンスを表示用のデータモデルに変換
        const customerDetail = convertApiResponseToBusinessCardDetail(response);
        
        // ランク情報の詳細をデバッグ
        
        // rankname プロパティが存在するか確認
        if (!response.customerdata?.rankname) {
          console.warn('詳細画面: rankname プロパティが存在しません！');
        }
        
        // 一覧画面と同じ方法でランク情報を取得
        // rankname プロパティのみを使用
        if (!customerDetail.rank && response.customerdata) {
          
          // 一覧画面と同じく rankname のみからランク情報を取得
          if (response.customerdata.rankname) {
            customerDetail.rank = response.customerdata.rankname;
          }
          
          // rankname がない場合は空文字をセット（一覧画面と同じ動作）
          if (!customerDetail.rank) {
            customerDetail.rank = '';
          }
        }
        
        
        // ランク色は一覧画面と同じ rank_colors マッピングから解決する
        try {
          if (customerDetail.rank) {
            setCurrentRankColor(getRankColorSync(customerDetail.rank, rankColors));
          } else {
            // ランクがない場合はデフォルト色
            setCurrentRankColor('#bfbfbf');
          }

          // 担当者番号を設定
          // contactusernoがある場合は、選択された担当者として設定
          try {
            if (customerDetail.contactuserno) {
              // 担当者番号の形式に応じて処理
              let contactUserIds: number[] = [];
              
              if (Array.isArray(customerDetail.contactuserno)) {
                // 配列の場合はそのまま使用
                contactUserIds = customerDetail.contactuserno.map(id => Number(id));
              } else if (typeof customerDetail.contactuserno === 'string') {
                // 文字列の場合はJSON配列をパース
                try {
                  contactUserIds = JSON.parse(customerDetail.contactuserno);
                } catch (e) {
                  // パースに失敗した場合は、カンマ区切りかもしれないので分割
                  contactUserIds = customerDetail.contactuserno
                    .replace(/[\[\]"']/g, '') // 角括弧とクォートを削除
                    .split(',')
                    .map(id => Number(id.trim()))
                    .filter(id => !isNaN(id));
                }
              } else if (typeof customerDetail.contactuserno === 'number') {
                // 数値の場合は単一の要素として配列に変換
                contactUserIds = [customerDetail.contactuserno];
              }
              
              // 有効な担当者IDのみを抽出（sys は負の userno なので 0 のみ除外）
              const validUserIds = contactUserIds.filter(id => !isNaN(id) && id !== 0);

              setSelectedContactUsers(validUserIds);
            } else {
              // 担当者が設定されていない場合は空配列
              setSelectedContactUsers([]);
            }
          } catch (error) {
            console.error('担当者IDの設定エラー:', error);
            // エラー時も空配列を設定
            setSelectedContactUsers([]);
          }
          
          // 顧客データをセット
          setCardData(customerDetail);
          
          // フォームに初期値をセット
          form.setFieldsValue(customerDetail);
          
          // 拡張フィールドの既存値を読み込み（拡張フィールドが既に読み込まれている場合）
          if (customerDetail.optionfield && optionFields?.customeroptionfieldlist) {
            try {
              const existingOptionField = JSON.parse(customerDetail.optionfield);
              
              const existingValues: Record<string, any> = {};
              optionFields.customeroptionfieldlist.forEach(field => {
                if (existingOptionField[field.optionfieldkey] !== undefined) {
                  existingValues[field.optionfieldkey] = existingOptionField[field.optionfieldkey];
                }
              });
              
              if (Object.keys(existingValues).length > 0) {
                setOptionFieldValues(existingValues);
              }
            } catch (error) {
              console.error('BusinessCardDetailPage - 拡張フィールド値の読み込みエラー:', error);
            }
          }
          
          // 初期表示がフォロー履歴タブの場合、APIからフォロー履歴を取得
          if (activeTab === '2') {
            fetchFollowHistory(id);
          }
        } catch (rankError) {
          // エラー処理...
          console.error('ランク情報取得エラー:', rankError);
          setCardData(customerDetail);
          form.setFieldsValue(customerDetail);
          
          // 拡張フィールドの既存値を読み込み（エラー時）
          if (customerDetail.optionfield && optionFields?.customeroptionfieldlist) {
            try {
              const existingOptionField = JSON.parse(customerDetail.optionfield);
              
              const existingValues: Record<string, any> = {};
              optionFields.customeroptionfieldlist.forEach(field => {
                if (existingOptionField[field.optionfieldkey] !== undefined) {
                  existingValues[field.optionfieldkey] = existingOptionField[field.optionfieldkey];
                }
              });
              
              if (Object.keys(existingValues).length > 0) {
                setOptionFieldValues(existingValues);
              }
            } catch (error) {
              console.error('BusinessCardDetailPage - 拡張フィールド値の読み込みエラー（エラー時）:', error);
            }
          }
        }
      } else {
        // エラー処理...
        console.error('顧客詳細情報取得エラー:', response);
        message.error('顧客情報の取得に失敗しました');
      }
    } catch (error) {
      // エラー処理...
      console.error('BusinessCardDetailPage - 顧客詳細情報取得エラー:', error);
      message.error('顧客情報の取得に失敗しました');
    }
    console.log('BusinessCardDetailPage - fetchData終了');
  };

  // IndexedDBから詳細表示する顧客IDを取得
  // URLパラメータは使用せず、セキュリティを向上させる
  const getCustomerId = useCallback(async (): Promise<number> => {
    try {
      const customerId = await getCurrentDetailCustomerId();
      if (customerId === null) {
        message.error('顧客情報の取得に失敗しました');
        navigate(getRoutePath('/business-card-list'));
        return 0; // ここには到達しないはずだが、TypeScript対応のため
      }
      return customerId;
    } catch (error) {
      console.error('顧客ID取得エラー:', error);
      message.error('顧客情報の取得に失敗しました');
      navigate(getRoutePath('/business-card-list'));
      return 0; // ここには到達しないはずだが、TypeScript対応のため
    }
  }, [navigate]);
  
  // 担当者番号から担当者名を取得する関数
  const getContactUserNames = (contactUserNo: any): string => {
    // contactusernoの形式によって処理を分ける
    let userIds: number[] = [];
    
    try {
      if (Array.isArray(contactUserNo)) {
        // 配列の場合はそのまま使用
        userIds = contactUserNo.map(id => Number(id));
      } else if (typeof contactUserNo === 'string') {
        // 文字列の場合はJSON配列をパース
        try {
          userIds = JSON.parse(contactUserNo);
        } catch (e) {
          // パースに失敗した場合は、カンマ区切りかもしれないので分割
          userIds = contactUserNo
            .replace(/[\[\]"']/g, '') // 角括弧とクォートを削除
            .split(',')
            .map(id => Number(id.trim()))
            .filter(id => !isNaN(id));
        }
      } else if (typeof contactUserNo === 'number') {
        // 数値の場合は単一の要素として配列に変換
        userIds = [contactUserNo];
      }
      
      // ユーザーIDから担当者名を取得（負の userno は sys ユーザーとして「システム管理者」表記）
      const names = userIds
        .map(id => {
          const user = contactUsers.find(u => u.userno === id);
          if (user) return user.username;
          if (id < 0) return 'システム管理者';
          return null;
        })
        .filter(name => name !== null);
      
      return names.join(', ') || '担当者なし';
    } catch (error) {
      console.error('担当者名の変換エラー:', error);
      return '担当者不明';
    }
  };

  
  // 担当者選択コンポーネントをレンダリングする関数
  const renderContactUsers = () => {
    // 編集モードでない、または担当者変更権限がなければ表示のみ
    if (!isEditing || !canEditContactUser) {
      return (
        <div>
          {getContactUserNames(selectedContactUsers)}
        </div>
      );
    }
    
    // 編集モード時の担当者選択UI
    return (
      <div>
        {/* 選択済み担当者のタグ表示 */}
        <div style={{ marginBottom: selectedContactUsers.length > 0 ? '8px' : 0 }}>
          {selectedContactUsers
            // 現在の担当者一覧に見つからない(退職者・削除済み)ものはシステムユーザー以外は表示しない
            .filter((userno) => userno < 0 || contactUsers.some(u => u.userno === userno))
            .map((userno) => {
            const user = contactUsers.find(u => u.userno === userno);
            return (
              <Tag
                key={userno}
                style={{
                  padding: '4px 8px',
                  marginBottom: '6px',
                  fontSize: '14px',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}
                closable={selectedContactUsers.length > 1}
                onClose={(e) => {
                  e.preventDefault();
                  // 担当者を削除（複数担当者がある場合のみ）
                  if (selectedContactUsers.length > 1) {
                    setSelectedContactUsers(prev => prev.filter(id => id !== userno));
                  }
                }}
              >
                {user ? user.username : 'システム管理者'}
              </Tag>
            );
          })}
        </div>
        
        {/* 担当者追加用のドロップダウン */}
        <Select
          style={{ width: '100%', marginTop: '8px' }}
          placeholder="+ 担当者を追加"
          onChange={(value) => {
            if (value && !selectedContactUsers.includes(Number(value))) {
              // 選択された担当者を追加
              setSelectedContactUsers(prev => [...prev, Number(value)]);
            }
          }}
          value={null} // 常に未選択状態を維持
        >
          {contactUsers
            .filter(user => !selectedContactUsers.includes(user.userno))
            .map(user => (
              <Select.Option key={user.userno} value={user.userno}>
                {user.username} ({user.deptname || '-'})
              </Select.Option>
            ))
          }
        </Select>
      </div>
    );
  };
  
  // アシスト入力フォームを初期化する関数
  const initializeAssistForm = () => {
    try {
      // 選択されたら少し待ってから初期化する
      
      // 質問リストの存在チェック
      if (!(window as any).enqQuestions) {
        return;
      }
      
      // まず要素の存在を確認
      let aptoenqform = document.getElementById('aptoenqform');
      
      // モーダル内の表示エリアを探す
      if (!aptoenqform) {
        // FollowRegistrationPageと同様に探す
        const container = document.querySelector('.ant-modal-body [style*="border"]');
        if (container) {
          // 既存の要素を確認
          const existingAptoenq = container.querySelector('#aptoenqform');
          if (existingAptoenq) {
            aptoenqform = existingAptoenq as HTMLElement;
          } else {
            // aptoenqformが存在しない場合は新しく作成
            aptoenqform = document.createElement('div');
            aptoenqform.id = 'aptoenqform';
            aptoenqform.className = 'fontM';
            
            // 新しい要素を配置
            container.innerHTML = ''; // 既存のコンテンツをクリア
            container.appendChild(aptoenqform);
          }
        } else {
          message.error('フォーム表示エリアが見つかりません。再度試してください。');
          return;
        }
      }
      
      // 要素の内容をクリア
      if (aptoenqform) {
        aptoenqform.innerHTML = '';
      }
      
      // 質問リストの確認と処理
      let processedQuestions = [];
      
      if (Array.isArray((window as any).enqQuestions)) {
        processedQuestions = (window as any).enqQuestions;
      } else if (typeof (window as any).enqQuestions === 'string') {
        try {
          processedQuestions = JSON.parse((window as any).enqQuestions);
        } catch(e) {
          processedQuestions = [];
        }
      } else if ((window as any).enqQuestions && typeof (window as any).enqQuestions === 'object') {
        // オブジェクトの場合は値を使用
        const objValues = Object.values((window as any).enqQuestions);
        if (objValues.length > 0) {
          processedQuestions = objValues;
        }
      }
      
      // 質問リストデータを表示（デバッグ用）
      if (processedQuestions.length === 0) {
        message.error('質問が見つかりません');
        return;
      }
      
      // CSSデータの処理
      let cssData = [];
      if (typeof (window as any).enqCss === 'string' && (window as any).enqCss.trim() !== '') {
        // 文字列のCSSをセレクタと値のペアに変換する試み
        try {
          // CSSを簡易的に解析（完全ではありませんが基本的なケースに対応）
          const cssRules = (window as any).enqCss.match(/([^{]+)\s*{\s*([^}]+)\s*}/g) || [];
          cssData = cssRules.map((rule: string) => {
            const match = rule.match(/([^{]+)\s*{\s*([^}]+)\s*}/) || [];
            return [match[1]?.trim(), match[2]?.trim()];
          });
        } catch(e) {
          console.error('CSSの解析に失敗:', e);
        }
      } else if (Array.isArray((window as any).enqCss)) {
        cssData = (window as any).enqCss;
      }
      
      // 再度要素の存在を確認（念のため）
      aptoenqform = document.getElementById('aptoenqform');
      if (!aptoenqform) {
        message.error('フォーム表示要素の準備に失敗しました');
        return;
      }
      
      // 質問が適切な形式（Array）であることを確認
      if (!Array.isArray(processedQuestions)) {
        message.error('質問データが正しい形式ではありません');
        return;
      }
      
      try {
        // AssistInputStateインスタンスを作成して初期化
        const state = new AssistInputState(processedQuestions, cssData);
        
        // グローバル変数にStateをセット
        (window as any).State = AssistInputState;
        
        // リセットを呼び出して初期表示
        state.reset();
      } catch (e) {
        message.error('フォームの初期化に失敗しました');
        console.error('フォーム初期化エラー:', e);
      }
      
      // ボタンを有効化
      const resetBtn = document.getElementById('selectEnqueteInputAid_btnReset');
      if (resetBtn) {
        resetBtn.removeAttribute('disabled');
      }
      
      const selectBtn = document.getElementById('selectEnqueteInputAid_btnSelect');
      if (selectBtn) {
        selectBtn.removeAttribute('disabled');
      }

      // アシスト入力内のリンクを標準ブラウザで開くようにイベントリスナーを追加
      setTimeout(() => {
        const aptoenqform = document.getElementById('aptoenqform');
        if (aptoenqform) {
          const handleLinkClick = (e: Event) => {
            const target = e.target as HTMLElement;
            if (target.tagName === 'A' && target.hasAttribute('href')) {
              const href = target.getAttribute('href');
              if (href && href !== '#' && (href.startsWith('http://') || href.startsWith('https://'))) {
                e.preventDefault();
                openUrl(href);
              }
            }
          };
          aptoenqform.removeEventListener('click', handleLinkClick);
          aptoenqform.addEventListener('click', handleLinkClick);
        }
      }, 100);
    } catch (e) {
      message.error('初期化中にエラーが発生しました');
      console.error('アシスト入力フォームの初期化に失敗:', e);
    }
  };
  
  // アシストオプション選択ハンドラ
  const handleAssistOptionChange = (value: number) => {
    setSelectedAssistEnq(value);

    // エラー表示をクリア
    setAssistModalError('');

    // フェッチと初期化は useEffect内のAPIコール完了後に行われるため、
    // ここでは何もしない - 選択状態を変更するだけ
  };

  // アシストテキストを挿入する関数
  const insertAssistText = (text: string) => {
    try {
      // 編集モードの場合はフォームフィールドに挿入
      if (isEditing) {
        // フォームから現在の備考を取得
        const currentRemarks = form.getFieldValue('remarks') || '';
        
        // テキストエリアの参照がある場合
        if (textAreaRef.current) {
          const textarea = textAreaRef.current?.resizableTextArea?.textArea;
          if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            
            // テキストを現在のカーソル位置に挿入
            const newValue = currentRemarks.substring(0, start) + text + currentRemarks.substring(end);
            
            // フォームに新しい値をセット
            form.setFieldsValue({ remarks: newValue });
            
            // モーダルを閉じる
            setAssistModalVisible(false);
            
            // フォーカスを戻す（遅延させてUIの更新後に実行）
            setTimeout(() => {
              if (textarea) {
                textarea.focus();
                // カーソル位置を挿入したテキストの後に設定
                const newPosition = start + text.length;
                textarea.setSelectionRange(newPosition, newPosition);
              }
            }, 100);
            
            // メッセージを表示してユーザーに知らせる
            message.success('テキストを挿入しました');
          } else {
            // フォールバック：テキスト参照なしの場合
            const newValue = currentRemarks + (currentRemarks ? '\n' : '') + text;
            form.setFieldsValue({ remarks: newValue });
            setAssistModalVisible(false);
            message.success('テキストを挿入しました');
          }
        } else {
          // テキストエリアの参照がない場合は末尾に追加
          const newValue = currentRemarks + (currentRemarks ? '\n' : '') + text;
          form.setFieldsValue({ remarks: newValue });
          setAssistModalVisible(false);
          message.success('テキストを挿入しました');
        }
      }
    } catch (error) {
      console.error('テキスト挿入中にエラーが発生しました:', error);
      message.error('テキストの挿入に失敗しました');
      setAssistModalVisible(false);
    }
  };

  // キーワード一覧を取得
  useEffect(() => {
    const fetchKeywordOptions = async () => {
      try {
        setIsLoadingKeywords(true);
        setKeywordLoadError(false);
        if (FORCE_API_ERROR) throw new Error('DEBUG: forced error');
        // キーワード一覧を取得 (configFunctionDiv=01 備考欄用)
        const keywordResult = await additionalAPI.getKeywordList("01");
        if (keywordResult.success && keywordResult.data) {
          try {
            // keywords が JSON 文字列の場合はパースする
            let keywordData = keywordResult.data;
            if (typeof keywordData === 'string') {
              keywordData = JSON.parse(keywordData);
            }
            
            // keywordlistプロパティがある場合（新しいAPI形式）
            if (keywordData.keywordlist && Array.isArray(keywordData.keywordlist)) {
              // キーワードオブジェクトをそのまま設定（keywordとkeywordcontentプロパティを保持）
              setKeywordOptions(keywordData.keywordlist);
            }
            // 旧APIの形式（result配列がある場合）
            else if (keywordData.result && Array.isArray(keywordData.result)) {
              const formattedKeywords = keywordData.result.map((item: any) => ({
                keyword: item.keyword || '',
                keywordcontent: item.keywordcontent || item.keyword || ''
              }));
              setKeywordOptions(formattedKeywords);
            }
            // 配列が直接返される場合
            else if (Array.isArray(keywordData)) {
              const formattedKeywords = keywordData.map((item: any) => {
                if (typeof item === 'object' && item.keyword) {
                  return {
                    keyword: item.keyword || '',
                    keywordcontent: item.keywordcontent || item.keyword || ''
                  };
                } else {
                  return {
                    keyword: item || '',
                    keywordcontent: item || ''
                  };
                }
              });
              setKeywordOptions(formattedKeywords);
            } else {
              console.warn('APIから備考用キーワード一覧のフォーマットが不明です:', keywordData);
              message.error('キーワードの取得に失敗しました。');
              setKeywordLoadError(true);
              setKeywordOptions([]);
            }
          } catch (e) {
            console.error('キーワードデータの解析エラー:', e);
            message.error('キーワードの取得に失敗しました。');
            setKeywordLoadError(true);
            setKeywordOptions([]);
          }
        } else {
          console.error('備考用キーワード一覧取得エラー:', keywordResult.message);
          message.error('キーワードの取得に失敗しました。');
          setKeywordLoadError(true);
          setKeywordOptions([]);
        }
      } catch (error) {
        console.error('備考用キーワード一覧取得エラー:', error);
        message.error('キーワードの取得に失敗しました。');
        setKeywordLoadError(true);
        setKeywordOptions([]);
      } finally {
        setIsLoadingKeywords(false);
      }
    };
    
    fetchKeywordOptions();
  }, []);
  
  // 拡張フィールド一覧を取得
  useEffect(() => {
    const fetchOptionFields = async () => {
      try {
        setLoadingOptionFieldsData(true);
        const optionFieldResult = await optionFieldAPI.getOptionFieldList();
        
        if (optionFieldResult.statuscode === 200) {
          setOptionFields(optionFieldResult);

          // furiganaフィールドを抽出
          if (optionFieldResult.customeroptionfieldlist) {
            const furiganaFieldData = optionFieldResult.customeroptionfieldlist.find(
              (field: any) => field.optionfieldkey === 'furigana'
            );
            if (furiganaFieldData) {
              setFuriganaField(furiganaFieldData);
            }
          }

          // 拡張フィールド読み込み完了後、既存のoptionfield値があれば設定
          await applyExistingOptionFieldValues(optionFieldResult);
        } else {
          console.error('拡張フィールド取得エラー:', optionFieldResult.message);
        }
      } catch (error) {
        console.error('拡張フィールド取得API呼び出しエラー:', error);
      } finally {
        setLoadingOptionFieldsData(false);
      }
    };
    
    fetchOptionFields();
  }, []);
  
  // cardDataとoptionFieldsが両方とも読み込まれた時に既存値を適用
  useEffect(() => {
    if (cardData && optionFields?.customeroptionfieldlist) {
      applyExistingOptionFieldValues(optionFields);
    }
  }, [cardData, optionFields]);
  
  // アシスト入力一覧を取得（備考用）
  useEffect(() => {
    const fetchAssistOptions = async () => {
      setAssistOptionsError(false);
      try {
        if (FORCE_API_ERROR) throw new Error('DEBUG: forced error');
        // 備考用のアシストテキストを取得（customerRemarksまたは空文字）
        const result = await additionalAPI.getAssistTextList('customerRemarks');
        if (result.success && result.data) {
          // 取得したデータが配列であることを確認
          if (Array.isArray(result.data)) {
            // enqNo と enqName プロパティを持つオブジェクトの配列に変換
            if (typeof result.data[0] === 'string') {
              // 文字列の配列の場合はオブジェクトに変換
              setAssistOptions(result.data.map((text, index) => ({
                enqNo: index + 1,
                enqName: text
              })));
            } else {
              // すでにオブジェクトの配列の場合はそのまま使用
              setAssistOptions(result.data);
            }
            // デフォルトアシストNoをuserから取得し、一覧に存在するものだけセット
            try {
              const userStr = await indexedDBStorage.getItem('user');
              if (userStr) {
                const user = JSON.parse(userStr);
                const defaultNo: number | null = user?.defaultcustomerassistno ?? null;

                const isValid =
                  defaultNo !== null &&
                  result.data.some((opt: any) =>
                    typeof opt === 'string' ? false : opt.enqNo === defaultNo
                  );

                setDefaultCustomerAssistNo(isValid ? defaultNo : null);
              }
            } catch (e) {
              console.error('デフォルトアシストNo取得エラー:', e);
            }
          } else {
            message.error('アシストの取得に失敗しました。');
            setAssistOptionsError(true);
            setAssistOptions([]);
          }
        } else {
          message.error('アシストの取得に失敗しました。');
          setAssistOptionsError(true);
          setAssistOptions([]);
        }
      } catch (error) {
        console.error('アシストオプションの取得に失敗:', error);
        message.error('アシストの取得に失敗しました。');
        setAssistOptionsError(true);
        setAssistOptions([]);
      }
    };
    
    fetchAssistOptions();
  }, []);

  
  
  
  // 担当者一覧のロード完了時にログ出力
  useEffect(() => {
    // 担当者一覧が読み込まれた後でのみ実行
    if (contactUsers.length > 0) {
    }
  }, [contactUsers]);
  
  // 担当者一覧とカードデータが揃ったときに担当者情報をログ出力
  useEffect(() => {
    if (contactUsers.length > 0 && cardData?.contactuserno) {
      
      const contactUserNames = getContactUserNames(cardData.contactuserno);
    }
  }, [contactUsers, cardData]);
  
  // サービス内のAssistInputStateを使用するため、外部CSSは不要
  
  // 選択されたアシストオプションが変更されたときにAPIを呼び出す
  useEffect(() => {
    const fetchAssistContent = async () => {
      if (selectedAssistEnq !== null) {
        setLoadingAssistContent(true);
        try {
          // 選択したアシストEnqNoを更新（ID要素に設定）
          const hiddenInput = document.getElementById('selectEnqueteInputAid_followContent') as HTMLInputElement;
          if (hiddenInput) {
            hiddenInput.value = selectedAssistEnq.toString();
          }
          
          const result = await additionalAPI.getAssistText(selectedAssistEnq);

          if (result.success && result.data) {
            try {
              // データが文字列の場合はJSONパースを試みる
              let parsedObj;
              if (typeof result.data === 'string') {
                parsedObj = JSON.parse(result.data);
              } else {
                parsedObj = result.data;
              }
              
              // エラー確認
              if (parsedObj.statuscode && parsedObj.statuscode !== 200) {
                setAssistContentHtml('<p>アシストテキストの取得に失敗しました。</p>');
                return;
              }
              
              // 重要：取得したJSONオブジェクトをSelectEnqueteInputAidModalに直接設定
              const modalObj = (window as any).SelectEnqueteInputAidModal;
              if (modalObj) {
                modalObj.enquete = parsedObj;
              }
              
              // 質問リストの確認
              let questionList = [];
              if (parsedObj.questionlist) {
                if (Array.isArray(parsedObj.questionlist)) {
                  questionList = parsedObj.questionlist;
                } else {
                  try {
                    // 文字列の場合はJSON解析を試みる
                    if (typeof parsedObj.questionlist === 'string') {
                      questionList = JSON.parse(parsedObj.questionlist);
                    }
                  } catch (e) {
                    console.error('質問リストのパースエラー:', e);
                  }
                }
              } else if (parsedObj.questionInfoList) {
                // 古い形式のAPIレスポンス対応
                if (Array.isArray(parsedObj.questionInfoList)) {
                  questionList = parsedObj.questionInfoList;
                }
              }
              
              // CSS設定の確認
              let css = parsedObj.css || '';
              
              // APIからのCSSと質問HTMLを結合し、スタイルを追加
              const overrideCss = `<style>
  .aptoenq_qa { margin: 0 !important; }
  .whitespace { white-space: pre-wrap; overflow-wrap: break-word; }
  .aptoenq_addtext { max-width: 100%; width: 100%; box-sizing: border-box; }
  textarea { font-family: inherit; font-size: inherit; padding: 5px; border: 1px solid #d9d9d9; border-radius: 4px; width: 100%; max-width: 100%; box-sizing: border-box; }
  label { display: block; margin-bottom: 10px; width: 100%; }
  .aptoenq_a { width: 100%; overflow-x: hidden; }
  .assist-content { width: 100%; overflow-x: hidden; }
  .assist-content * { max-width: 100%; }
</style>`;
              
              // 簡単なプレースホルダーのHTMLだけ設定
              const placeholderHtml = `<style>${typeof css === 'string' ? css : ''}</style>${overrideCss}`;
              setAssistContentHtml(placeholderHtml);
              
              // グローバル変数として質問とCSSを保存
              (window as any).enqQuestions = questionList;
              (window as any).enqCss = css;
              
              // グローバルオブジェクトの設定
              // AdditionalInfoPageと同じ方法：グローバルオブジェクトの設定
              window.SelectEnqueteInputAidModal = SelectEnqueteInputAidModal;
              window.State = AssistInputState;
              
              // アシスト入力モーダル内でエラーを表示する関数を設定
              (window as any).showAssistErrorInModal = (errorText: string) => {
                setAssistModalError(errorText);

                // エラー表示位置にスクロール
                setTimeout(() => {
                  if (assistModalErrorRef.current) {
                    assistModalErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100);
              };
              
              // 静的クラスに設定
              SelectEnqueteInputAidModal.enquete = parsedObj;
              SelectEnqueteInputAidModal.kind = 'customerRemarks';

              // 選択されたアシストオプションのタイトルを設定
              const selectedOption = assistOptions.find(opt => opt.enqNo === selectedAssistEnq);
              if (selectedOption) {
                SelectEnqueteInputAidModal.selectedEnqName = selectedOption.enqName;
              }

              // AdditionalInfoPageと同じ方法：静的クラスのみにコールバックを設定
              SelectEnqueteInputAidModal.callback = (text: string) => {
                insertAssistText(text);
                setAssistModalVisible(false);
              };
              
              // aptoenqform要素を強制的にクリア
              const aptoenqform = document.getElementById('aptoenqform');
              if (aptoenqform) {
                aptoenqform.innerHTML = '';
              }
              
              // 少し待ってから初期化関数を呼び出す（レンダリング完了を待つ）
              setTimeout(() => {
                initializeAssistForm();
              }, 300);
            } catch (e) {
              console.error('アシストテキストデータの解析エラー:', e);
              setAssistContentHtml('<p>データの解析に失敗しました。</p>');
            }
          } else {
            setAssistContentHtml('<p>アシストテキストの取得に失敗しました。</p>');
          }
        } catch (error) {
          console.error('アシストテキスト取得エラー:', error);
          setAssistContentHtml('<p>アシストテキストの取得に失敗しました。</p>');
        } finally {
          setLoadingAssistContent(false);
        }
      } else {
        setAssistContentHtml('');
      }
    };

    fetchAssistContent();
  }, [selectedAssistEnq]);

  // 顧客IDに基づいてAPIからデータを取得
  useEffect(() => {
    // コンポーネントレベルの fetchData 関数を呼び出す
    fetchData();
    
    // 古いfetchData関数は削除、代わりにコンポーネントレベルのfetchDataを使用
    // ここにあった非同期関数はコンポーネントレベルに移動済み
    
    // 以下は削除されたコードの参考情報：
    // - 顧客IDの取得
    // - businessCardAPI.getCustomerDetailの呼び出し
    // - レスポンスの処理
    // - データの変換と設定
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, rankColors, getCustomerId]);
  
  const handleTabChange = (key: string) => {
    // フォロー履歴タブが選択された場合、APIからフォロー履歴を取得
    if (key === '2' && cardData) {
      fetchFollowHistory(cardData.id);
    }
    setActiveTab(key);
  };
  
  // 既存の拡張フィールド値を適用する関数
  const applyExistingOptionFieldValues = async (optionFieldData: OptionFieldListResponse) => {
    try {
      if (cardData?.optionfield && optionFieldData?.customeroptionfieldlist) {
        const existingOptionField = JSON.parse(cardData.optionfield);

        const existingValues: Record<string, any> = {};
        optionFieldData.customeroptionfieldlist.forEach(field => {
          if (existingOptionField[field.optionfieldkey] !== undefined) {
            existingValues[field.optionfieldkey] = existingOptionField[field.optionfieldkey];
          }
        });

        // furiganaフィールドの値があれば設定
        if (existingOptionField.furigana) {
          setFuriganaValue(existingOptionField.furigana);
        }

        if (Object.keys(existingValues).length > 0) {
          setOptionFieldValues(existingValues);
        }
      }
    } catch (error) {
      console.error('BusinessCardDetailPage - 既存拡張フィールド値適用エラー:', error);
    }
  };

  // 拡張フィールドの値変更ハンドラー
  const handleOptionFieldChange = (fieldKey: string, value: any) => {
    setOptionFieldValues(prev => {
      const newValues = {
        ...prev,
        [fieldKey]: value
      };
      return newValues;
    });
  };
  
  // ログインユーザーを担当者としてセットする関数
  const setLoginUserAsContact = () => {
    // AuthContextから取得したユーザー情報を使用
    if (user && user.userno) {
      
      // 担当者リストにログインユーザーが存在することを確認
      const userExists = contactUsers.some(
        contactUser => contactUser.userno.toString() === user.userno?.toString()
      );
      
      if (userExists) {
        setSelectedContactUsers([user.userno]);
      } else {
        // デフォルトで最初の担当者を選択
        if (contactUsers.length > 0) {
          setSelectedContactUsers([contactUsers[0].userno]);
        }
      }
    } else {
      // デフォルトで最初の担当者を選択
      if (contactUsers.length > 0) {
        setSelectedContactUsers([contactUsers[0].userno]);
      }
    }
  };
  
  // フォロー API リクエストデータを組み立てる共通ヘルパー
  // registerFollowData（ファイル添付用仮登録）と handleAddFollow（本登録）の両方で使用する
  const buildFollowRequestData = (vals: FollowFormValues, customerId: string, currentFollowNo?: number | null): any => {
    const data: any = {
      customerno: customerId,
      contactuserno: vals.assigneeUserno,
      followdate: vals.date + 'T00:00:00.000+0900',
      followcontent: vals.content,
      temporaryflag: vals.isProvisional,
      plantodoflag: vals.isTodo,
      optionfield: Object.keys(vals.optionFieldValues).length > 0
        ? JSON.stringify(vals.optionFieldValues)
        : undefined,
    };

    if (currentFollowNo) {
      data.followno = currentFollowNo.toString();
    }

    // 訪問/来社チェックがONの場合のみ関連フィールドを追加
    if (vals.isOnsite) {
      const [schedHour = '', schedMin = ''] = (vals.followScheduledTime || '').split(':');
      const [startHour = '', startMin = ''] = (vals.followStartTime || '').split(':');
      const [endHour = '', endMin = ''] = (vals.followEndTime || '').split(':');
      data.outgovisitflag = true;
      data.outgovisitschedultimehour = schedHour;
      data.outgovisitschedultimemin = schedMin;
      data.outgovisitappoint = vals.followAppointment || '';
      data.outgovisitpurpose = vals.followVisitPurpose || '';
      data.outgovisitstarttimehour = startHour;
      data.outgovisitstarttimemin = startMin;
      data.outgovisitendtimehour = endHour;
      data.outgovisitendtimemin = endMin;
      data.attendeesno = JSON.stringify(vals.onsiteCustomers.map(c => c.customerno).filter(Boolean));
      data.ourattendeesno = JSON.stringify(vals.selectedInternalAttendees);
    }

    return data;
  };

  // フォームの入力・アンカー state をクリアするヘルパー（モーダル開閉とは独立）。
  // followNo を null にすることで関連フォローのフック側 state も自動リセットされる。
  // latestEditRef も破棄し、リセット前に発行済みの selectFollow 応答が後から届いてもこのフォームを上書きしないようにする。
  const resetFollowFormState = () => {
    latestEditRef.current = null;
    setFollowNo(null);
    setFollowModalMode('new');
    setFollowAnchorMeta(null);
    setFollowEditComments([]);
    setOnsiteCustomers([]);
    setFileList([]);
    setAttachFiles([]);
    pendingFollowValuesRef.current = null;
    setFollowEditAlertSettings(undefined);
    setFollowNotifIsTodo(false);
  };

  // フォローモーダルを閉じてBCD側のstate をリセットするヘルパー
  const closeFollowModal = () => {
    setShowAddFollowModal(false);
    resetFollowFormState();
  };

  const {
    onAssistClick: followOnAssistClick,
    onVoiceClick: followOnVoiceClick,
    onFileAdd: followOnFileAdd,
    onContentSetterReady: followOnContentSetterReady,
    openFileUploadModal: followOpenFileUploadModal,
    renderModals: followRenderModals,
  } = useFollowFormFeatures({
    followno: followNo,
    baseZIndex: 1000,
    onAfterUpload: async () => {
      if (followNo) {
        try {
          const attachmentListResponse = await followAPI.getAttachmentList({ type: 1, targetno: followNo });
          if (attachmentListResponse.statuscode === 200 && attachmentListResponse.attachfilelist) {
            const apiFileList = attachmentListResponse.attachfilelist.map((file: any) => ({
              uid: file.attachfileno.toString(),
              name: file.attachfilename,
              status: 'done',
              size: parseFloat(file.attachfilesize) * 1024,
              type: file.contentType || 'application/octet-stream',
              attachfilekey: file.attachfilekey,
              attachfileno: file.attachfileno,
            }));
            setFileList(apiFileList);
          }
        } catch {}
        if (cardData?.id) await fetchFollowHistory(cardData.id);
      }
    },
    onFileAddWithoutFollowno: (values) => {
      pendingFollowValuesRef.current = values;
      setFileAddConfirmVisible(true);
    },
    flutterFooterHeight,
  });

  // ファイル添付用：フォローを仮保存して followId を取得する
  // formValues: FollowFormModal から受け取った値（pendingFollowValuesRef 経由）
  const registerFollowData = async (isForAttachment = false): Promise<number | null> => {
    const vals = pendingFollowValuesRef.current;
    if (!vals?.assigneeUserno) return null;

    try {
      // 共通ヘルパーで全フィールド（isOnsite・optionfield 含む）を組み立てる
      const requestData = buildFollowRequestData(vals, cardData?.id?.toString() || '', followNo);

      // フォロー情報をAPIに送信
      const response = await followAPI.registerFollow(requestData);

      if (response.success && response.data?.followno) {
        // 成功した場合、フォローNoを保存
        const followId = parseInt(response.data.followno);
        setFollowNo(followId);

        // ファイル添付のための仮登録でない場合は成功メッセージを表示
        if (!isForAttachment) {
          // 更新か新規登録かによってメッセージを変える
          message.success(followModalMode === 'edit' ? 'フォロー情報を更新しました' : 'フォロー情報を登録しました');

          // フォロー履歴を再取得して最新状態に更新
          if (cardData?.id) await fetchFollowHistory(cardData.id);

          // フォロー履歴タブに切り替え
          setActiveTab('2');

          // フォロー履歴タブにスクロール
          setTimeout(() => {
            followHistoryTabRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }

        return followId;
      } else {
        message.error('フォロー情報の登録に失敗しました');
        return null;
      }
    } catch (error) {
      console.error('フォロー登録エラー:', error);
      message.error('フォロー情報の登録に失敗しました');
      return null;
    }
  };

  // フォロー履歴を取得する関数
  const fetchFollowHistory = async (customerId: number) => {
    if (!customerId) return;
    
    try {
      setIsLoadingFollowHistory(true);
      
      // APIからフォロー履歴を取得
      const followList = await followAPI.searchFollowList({
        customerno: customerId,
        excludeTemporary: false // 仮登録も含めて取得
      });

      // フォロー日の新しい順（降順）にソート。同一の場合はcreatedateで降順
      const sortedList = followList.sort((a, b) => {
        const dateA = a.followdate || '';
        const dateB = b.followdate || '';
        const followDateDiff = new Date(dateB).getTime() - new Date(dateA).getTime();
        if (followDateDiff !== 0) {
          return followDateDiff;
        }
        // followdateが同一の場合、createdateで降順ソート
        const createDateA = a.createdate || '';
        const createDateB = b.createdate || '';
        return new Date(createDateB).getTime() - new Date(createDateA).getTime();
      });
      
      // フォロー履歴をフォーマットして設定
      const formattedHistory = sortedList.map((follow) => {
        const formattedDate = isoUtcToJstDate(follow.followdate || follow.follow_date || '');

        return {
          id: follow.followno || 0,
          // フィールド名の可能性を考慮
          date: formattedDate,
          // フィールド名の可能性を考慮
          content: follow.followcontent || follow.follow_content || follow.remarks || '',
          // 担当者が sys（userno が負）の場合は「システム管理者」と表示
          representative: followRepresentative(
            follow.contactuserno,
            follow.contactusername || follow.contact_username || follow.username || ''
          ),
          // 自フォロー判定用フィールド（生値のまま）
          ...followOwnerFields(follow),
          // フィールド名の可能性を考慮
          provisional: !!follow.temporaryflag || !!follow.temporary_flag || follow.provisional === 1,
          plantodo: !!follow.plantodoflag,
          // attach フラグ由来（初期表示では添付ファイル取得APIを叩かない）
          hasAttachment: follow.attach === true,
          attachments: undefined
        };
      });

      // 顧客データを更新
      setCardData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          followHistory: formattedHistory
        };
      });
      
    } catch (error) {
      console.error('フォロー履歴取得エラー:', error);
      message.error('フォロー履歴の取得に失敗しました');
    } finally {
      setIsLoadingFollowHistory(false);
    }
  };
  
  // ファイルプレビュー・ダウンロード処理
  const handleFilePreview = (file: any) => {
    previewAttachFile({ attachfileno: file.attachfileno, filename: file.name || '' });
  };

  const handleFileDownload = (file: any) => {
    downloadAttachFile({ attachfileno: file.attachfileno, filename: file.name || '' });
  };

  const handleBack = () => {
    navigate(getRoutePath('/business-card-list'));
  };
  
  const startEditing = () => {
    // APIエラーをクリア
    setApiError(null);
    // 編集開始時に現在のデータをフォームにセット
    if (cardData) {
      // ランクオプションが既に取得されているか確認
      if (rankOptions.length === 0) {
        console.warn('ランクオプションがまだ取得されていません');
      }
      
      // 現在のランク値とオプションを照合
      const currentRank = cardData.rank;
      const matchingRankOption = rankOptions.find(option => option.label === currentRank);
      
      // フォームにデータをセット
      const formData = {
        ...cardData,
        // ランク値は、一致するオプションがあればその値を使用、なければそのまま使用
        rank: matchingRankOption ? matchingRankOption.value : currentRank
      };
      
      form.setFieldsValue(formData);
    }
    
    setIsEditing(true);

    // 詳細情報タブに切り替え
    setActiveTab('1');

    // 詳細情報部分へスクロール
    setTimeout(() => {
      // タブコンテナにスクロール
      followHistoryTabRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 200); // タブ切り替えと編集モードの表示切り替えを待ってからスクロール
  };

  // バリデーション関数
  const validatePhoneOrEmail = () => {
    const allValues = form.getFieldsValue();
    const hasPhone = allValues.phone && allValues.phone.trim() !== '';
    const hasEmail = allValues.email && allValues.email.trim() !== '';

    if (!hasPhone && !hasEmail) {
      setFormValidity(prev => ({
        ...prev,
        phone: { valid: false, message: '電話番号またはメールアドレスのいずれかを入力してください' },
        email: { valid: false, message: '電話番号またはメールアドレスのいずれかを入力してください' }
      }));
    } else {
      // どちらかが入力されていればエラーをクリア
      setFormValidity(prev => {
        const newValidity = { ...prev };
        if (newValidity.phone) delete newValidity.phone;
        if (newValidity.email) delete newValidity.email;
        return newValidity;
      });
    }
  };

  // 顧客名のバリデーション
  const handleNameBlur = () => {
    const allValues = form.getFieldsValue();
    if (!allValues.name || allValues.name.trim() === '') {
      setFormValidity(prev => ({
        ...prev,
        name: { valid: false, message: '顧客名を入力してください' }
      }));
    } else {
      setFormValidity(prev => {
        const newValidity = { ...prev };
        if (newValidity.name) delete newValidity.name;
        return newValidity;
      });
    }
  };

  // 電話番号のバリデーション
  const handlePhoneBlur = () => {
    validatePhoneOrEmail();
  };

  // メールアドレスのバリデーション
  const handleEmailBlur = () => {
    validatePhoneOrEmail();
  };

  // フォーム値変更時にエラーをクリア
  const handleFormValuesChange = (changedValues: any) => {
    const changedField = Object.keys(changedValues)[0];
    if (changedField && formValidity[changedField]) {
      setFormValidity(prev => {
        const newValidity = { ...prev };
        delete newValidity[changedField];
        return newValidity;
      });
    }
  };

  const saveChanges = async () => {
    // フォームの値を取得
    const values = form.getFieldsValue();

    // カスタムバリデーション: 顧客名必須チェック
    if (!values.name || values.name.trim() === '') {
      message.error('顧客名を入力してください');
      return;
    }

    // カスタムバリデーション: 電話番号 OR メールアドレス
    const hasPhone = values.phone && values.phone.trim() !== '';
    const hasEmail = values.email && values.email.trim() !== '';

    if (!hasPhone && !hasEmail) {
      message.error('電話番号またはメールアドレスのいずれかを入力してください');
      return;
    }

    try {
      // 拡張フィールドのバリデーション
      if (optionFields?.customeroptionfieldlist) {
        const optionValidationResult = OptionFieldValidator.validateFields(
          optionFieldValues,
          optionFields.customeroptionfieldlist
        );

        if (!optionValidationResult.isValid) {
          optionValidationResult.errors.forEach(error => {
            message.error(error.message);
          });
          return;
        }
      }

      // 更新処理中のローディング状態を設定
      setIsSaving(true);
      // APIエラーをクリア
      setApiError(null);

      // ランク値を適切に変換
      let processedValues = { ...values };

      // ランク値を適切に処理
      if (values.rank) {
        // ランク値が選択されている場合、対応するラベルを取得
        const selectedRankOption = rankOptions.find(option => option.value === values.rank);
        if (selectedRankOption) {
          // ラベルをランク値として保存
          processedValues.rank = selectedRankOption.label;
        } else {
          // 選択肢が見つからない場合は値をそのまま使用
          processedValues.rank = values.rank;
        }
      } else {
        // 値が選択されていない場合は明示的に空文字をセット
        processedValues.rank = '';
      }

      // EditResultPageのAPI呼び出しを参考に、必要な最小限のデータだけを送信
      const apiData: any = {
        customerno: cardData?.id.toString() || "0",  // 顧客番号（必須）
        companyname: processedValues.company || "",
        customername: processedValues.name || "",    // 修正: name → customername
        delflag: false,  // 削除フラグは常にfalse（JSON形式ではboolean型）
        batchcreateflg: false // バッチ作成フラグもfalse（JSON形式ではboolean型）
      };

      // 追加の基本情報 - 空欄のフィールドは送信しない (SQLエラー防止)
      if (processedValues.phone) apiData.tel = processedValues.phone;               // 修正: telephone → tel
      if (processedValues.email) apiData.mailaddress = processedValues.email;       // 修正: emailaddress → mailaddress
      if (processedValues.mobile) apiData.mobiletel = processedValues.mobile;       // 修正: mobiletelno → mobiletel
      if (processedValues.address) apiData.address = processedValues.address;
      if (processedValues.remarks) apiData.remarks = processedValues.remarks;
      if (processedValues.url) apiData.url = processedValues.url;
      if (processedValues.fax) apiData.fax = processedValues.fax;
      if (processedValues.country) apiData.countryname = processedValues.country;
      // 配信NGフィールドを追加（maildeliveryflagとして保存）
      if (processedValues.doNotDistribute !== undefined) {
        apiData.maildeliveryflag = !processedValues.doNotDistribute;  // 逆にする
      } else {
        apiData.maildeliveryflag = true;  // デフォルトはOK
      }

      // ランク情報 - 常に明示的に設定する
      apiData.ranklabel = processedValues.rank || "";
      apiData.rankname = processedValues.rank || "";  // ranknameも設定
          
          // ranknoは数値型として保存（文字列の場合は数値に変換）
      if (values.rank) {
        // 元のフォーム値から数値部分を取得
        const rankno = typeof values.rank === 'string' ? parseInt(values.rank, 10) : values.rank;
        apiData.rankno = !isNaN(rankno) ? rankno : 0;  // 数値に変換できなければ0
      } else {
        apiData.rankno = 0;  // デフォルト値
      }

      // 役職と部署
      if (processedValues.position) apiData.position = processedValues.position;
      if (processedValues.department) apiData.deptname = processedValues.department; // 修正: department → deptname

      // オプションフィールド（既存の値があればそのまま、なければ空のオブジェクト）
      if (cardData && (cardData as any).optionfield) {
        apiData.optionfield = (cardData as any).optionfield;
      } else {
        apiData.optionfield = JSON.stringify({});
      }

      // 担当者番号 - selectedContactUsersを使用
      if (selectedContactUsers.length > 0) {
        // 選択された担当者IDを配列としてJSON文字列化
        apiData.contactuserno = JSON.stringify(selectedContactUsers);
      } else if (user?.id) {
        // 担当者が選択されていない場合は、ログインユーザーIDをデフォルト値として設定
        // ※ SQL構文エラーを防ぐために常に最低1つの要素を持つ配列を保証
        const defaultUserNo = user.id;
        apiData.contactuserno = JSON.stringify([defaultUserNo]);
      } else {
        // 担当者もログインユーザーも取得できない場合は空配列ではなく1を設定
        apiData.contactuserno = JSON.stringify([1]);
      }

      // 拡張フィールドの値を追加
      if (Object.keys(optionFieldValues).length > 0 || furiganaField) {
        // 既存のoptionfieldと新しい値をマージ
        let mergedOptionField: any = {};

        // 既存のoptionfieldを取得
        if (cardData?.optionfield) {
          try {
            mergedOptionField = JSON.parse(cardData.optionfield);
          } catch (error) {
            console.error('既存optionfield解析エラー:', error);
          }
        }

        // 拡張フィールドの値をマージ
        mergedOptionField = { ...mergedOptionField, ...optionFieldValues };

        // フリガナの値を保存（フリガナフィールドが存在する場合のみ）
        if (furiganaField) {
          mergedOptionField.furigana = furiganaValue || '';
        }

        apiData.optionfield = JSON.stringify(mergedOptionField);
      }


      // EditResultPageやAdditionalInfoPageと同様にAPI呼び出し
      const response = await businessCardAPI.editCustomer(apiData);

      console.log('BusinessCardDetailPage - APIレスポンス:', response);

      // API レスポンスの検証 - EditResultPageと同じくstatuscodeのみで判定
      if (response.statuscode === 200) {
        message.success('顧客情報が更新されました');

        // 更新された情報を明示的に取り出す
        // デバッグ用ログ

        const updatedBusinessCard: BusinessCardDetail = {
          ...cardData,
          ...processedValues,
        } as BusinessCardDetail;

        // 拡張フィールドの値も更新されたデータに含める
        if (Object.keys(optionFieldValues).length > 0) {
          let mergedOptionField = {};

          // 既存のoptionfieldを取得
          if (cardData?.optionfield) {
            try {
              mergedOptionField = JSON.parse(cardData.optionfield);
            } catch (error) {
              console.error('既存optionfield解析エラー:', error);
            }
          }

          // 拡張フィールドの値をマージ
          mergedOptionField = { ...mergedOptionField, ...optionFieldValues };
          updatedBusinessCard.optionfield = JSON.stringify(mergedOptionField);
        }

        // ランク情報が適切に設定されているか確認

        // ランク変更の確認 - より寛容な条件に変更（常にランク情報を処理）

        // ランクが設定されている場合は常にランク色を更新
        if (processedValues.rank) {

          // ranknoの値を直接使用（数値に変換）
          // 文字列の場合は数値に変換、数値の場合はそのまま使用
          const rankno = typeof values.rank === 'string' ? parseInt(values.rank, 10) : values.rank;

          // ranknoの値を直接使用してインデックスとする
          // 不正な値の場合は-1とする
          const rankIndex = !isNaN(rankno) && rankno >= 0 ? rankno : -1;

          // getRankColorByIndex関数を使用して色を取得
          const newRankColor = getRankColorByIndex(rankIndex);

          // ランク色を設定
          setCurrentRankColor(newRankColor);

          // 一覧画面と同じ表示になるよう、顧客IDとランク情報（インデックス含む）を関連付けて保存
          if (cardData?.id) {
            // ranknoとランク名を保存
            const apiData = {
              customerno: cardData.id.toString(),
              rankno: rankIndex >= 0 ? rankIndex : undefined,  // 有効な値のみ保存
              rankname: processedValues.rank
            };

            // ランク情報を保存
            saveCustomerRank(cardData.id, processedValues.rank, rankIndex, newRankColor)
              .then(() => {})
              .catch(err => console.error('ランク情報の保存に失敗:', err));

            // ローカルストレージにも同じ情報を保存（即時反映のため）
            try {
              // グローバルなランク色マッピングも更新
              const updatedRankColors = { ...rankColors };
              updatedRankColors[processedValues.rank] = newRankColor;
              setRankColors(updatedRankColors);
            } catch (e) {
              console.error('ローカルランク色の更新に失敗:', e);
            }
          }

          // 明示的に updatedBusinessCard のランク情報を設定
          updatedBusinessCard.rank = processedValues.rank;
          // ranknoも設定
          updatedBusinessCard.rankno = rankIndex;
        } else if (cardData?.rank) {
          // processedValuesにランクがなくても、元のデータにあればそれを維持
          updatedBusinessCard.rank = cardData.rank;

          // 既存の色も維持
        }

        // ローカルのデータを更新
        setCardData(updatedBusinessCard);

        // フォームの値も更新（編集が終わったあとの表示用）
        form.setFieldsValue(updatedBusinessCard);

        // 編集モードを終了
        setIsEditing(false);

        // fetchData()は呼び出さない - すでに更新済みのデータを使用する
      } else {
        // エラーメッセージを設定
        const errorMessage = response.message || '更新に失敗しました';
        setApiError(errorMessage);
        console.error('API更新エラー:', response);
        // エラー時に画面の一番上にスムーススクロール
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('更新処理中にエラーが発生しました:', error);
      const errorMessage = error instanceof Error ? error.message : '更新処理中にエラーが発生しました';
      setApiError(errorMessage);
      // エラー時に画面の一番上にスムーススクロール
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSaving(false);
    }
  };
  
  const cancelEditing = () => {
    // APIエラーをクリア
    setApiError(null);
    // 編集をキャンセルし、元の値に戻す
    form.setFieldsValue(cardData);
    if (cardData && cardData.optionfield) {
      try {
        const json = JSON.parse(cardData.optionfield);
        setFuriganaValue(json?.furigana);
        setOptionFieldValues(json);
      } catch (e) {
        console.error("optionfieldのパースに失敗しました", e);
      }
    }
    setIsEditing(false);
  };

  const deleteChanges = async () => {

    try {
      // 削除処理中のローディング状態を設定
      setIsDeleting(true);
      // APIエラーをクリア
      setApiError(null);

      // EditResultPageのAPI呼び出しを参考に、必要な最小限のデータだけを送信
      const apiData: any = {
        customerno: cardData?.id.toString() || "0",  // 顧客番号（必須）
        delflag: true,  // 削除フラグは常にtrue（JSON形式ではboolean型）
        batchcreateflg: false // バッチ作成フラグもfalse（JSON形式ではboolean型）
      };

      // EditResultPageやAdditionalInfoPageと同様にAPI呼び出し
      const response = await businessCardAPI.editCustomer(apiData);

      console.log('BusinessCardDetailPage - APIレスポンス:', response);

      // API レスポンスの検証 - EditResultPageと同じくstatuscodeのみで判定
      if (response.statuscode === 200) {
        message.success('顧客情報が削除されました');

        // 削除モードを終了
        setIsDeleting(false);

        // 名刺一覧画面に遷移
        handleBack();

      } else {
        // エラーメッセージを設定
        const errorMessage = response.message || '顧客削除に失敗しました';
        setApiError(errorMessage);
        console.error('API顧客削除エラー:', response);
        // エラー時に画面の一番上にスムーススクロール
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('顧客削除処理中にエラーが発生しました:', error);
      const errorMessage = error instanceof Error ? error.message : '顧客削除処理中にエラーが発生しました';
      setApiError(errorMessage);
      // エラー時に画面の一番上にスムーススクロール
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsDeleting(false);
    }
  };

  // 録音を開始する関数

  // FollowFormModal から送信が確定したとき（バリデーション済み）
  // → 通知先設定モーダルを表示する
  const handleFollowFormSubmit = async (values: FollowFormValues) => {
    if (!cardData) return;
    pendingFollowValuesRef.current = values;
    setFollowNotifIsTodo(values.isTodo);
    setFollowNotifInitialRecipients(
      await buildInitialNotifRecipients(cardData.id, values.assigneeUserno, user?.userno, contactUsers, values.isTodo)
    );
    // TODO: Teams通知実装時はコメントアウトを解除（Teamsチャネル一覧の先取得）
    // const channels = await followAPI.getTeamsChannels(cardData.id);
    // setFollowNotifTeamsChannels(channels);
    setFollowNotifModalVisible(true);
  };

  // 通常登録用：フォローを本登録して画面を完了状態にする（通知先設定モーダル確定後に呼ばれる）
  const handleAddFollow = async (
    notifRecipients: { userno: number; urgent: boolean }[] = [],
    alertSettings?: AlertSettings,
    // TODO: メールアドレス通知実装時はコメントアウトを解除
    // emailAddresses: string[] = [],
    // TODO: Teams通知実装時はコメントアウトを解除
    // teamsChannelUrls: string[] = []
  ) => {
    const vals = pendingFollowValuesRef.current;
    if (!vals || !cardData) return;

    try {
      // 共通ヘルパーで全フィールド（isOnsite・optionfield 含む）を組み立てる
      const followData = buildFollowRequestData(vals, cardData.id.toString(), followNo);

      // アラート設定を追加（予定/ToDoが有効な場合のみ）
      if (alertSettings) {
        followData.prealert = alertSettings.preAlert;
        followData.realert = alertSettings.reAlert;
        followData.realertflag = alertSettings.reAlertFlag;
      }

      // APIを呼び出してフォロー情報を登録
      const response = await followAPI.registerFollow(followData);

      if (response.success) {
        message.success(followModalMode === 'edit' ? 'フォロー情報を更新しました' : 'フォロー情報を登録しました');

        // 通知先（ユーザー）が指定されている場合は通知APIを呼び出す
        // TODO: Teams・メールアドレス通知実装時は以下の条件に `|| teamsChannelUrls.length > 0 || emailAddresses.length > 0` を追加
        const hasRecipients = notifRecipients.length > 0;
        if (hasRecipients) {
          // 登録済み followno を取得（編集時は既存の followNo、新規時はレスポンスから取得）
          const registeredFollowNo =
            followNo ??
            Number(response.data?.followno ?? response.data?.followdata?.followno ?? 0);

          if (registeredFollowNo) {
            try {
              // TODO: Teams・メールアドレス通知実装時は buildNoticeDestinationList(notifRecipients, emailAddresses, teamsChannelUrls) に復元
              const noticeDestinationList = buildNoticeDestinationList(notifRecipients);
              const alertParams = vals.isTodo && alertSettings
                ? {
                    plantodoflag: true,
                    prealert: alertSettings.preAlert,
                    realert: alertSettings.reAlert,
                    realertflag: alertSettings.reAlertFlag,
                  }
                : undefined;
              const warnMsg = await followAPI.sendNotice(registeredFollowNo, noticeDestinationList, alertParams);
              // Teams通知失敗時はサーバーから警告メッセージが返る（DB登録は完了）
              if (warnMsg) message.warning(warnMsg);
            } catch {
              // 通知失敗はフォロー登録の成否に影響させない
              message.warning('フォロー情報は登録されましたが、通知の送信に失敗しました');
            }
          }
        }

        // フォロー履歴を再取得して最新状態に更新
        await fetchFollowHistory(cardData.id);
        // フォロー履歴タブに切り替え
        setActiveTab('2');
        // フォロー履歴タブにスクロール
        setTimeout(() => {
          followHistoryTabRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        closeFollowModal();
      } else {
        message.error(response.message || 'フォロー情報の登録に失敗しました');
      }
    } catch (error) {
      console.error('フォロー登録エラー:', error);
      message.error('フォロー情報の登録に失敗しました');
    }
  };

  /** フォロー履歴: 添付ファイルのキャッシュ保存 */
  const handleFollowAttachmentLoad = (id: number, attachments: FollowHistoryAttachment[]) => {
    setCardData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        followHistory: prev.followHistory?.map(h =>
          h.id === id ? { ...h, attachments } : h
        )
      };
    });
  };

  /** フォロー履歴: 添付ファイルモーダルを開く */
  const handleFollowAttachmentViewClick = (files: FollowHistoryAttachment[], index: number) => {
    setHistoryAttachModalFiles(files);
    setHistoryAttachModalFollowIndex(index);
    setHistoryAttachModalVisible(true);
  };

  /** フォロー履歴: 編集ボタン */
  const handleEditFollowItem = async (itemId: number) => {
    latestEditRef.current = itemId;
    try {
      const follow = await followAPI.selectFollow(itemId);
      // 連打時: 後着応答が別フォローの編集内容を上書きしないよう破棄
      if (latestEditRef.current !== itemId) return;
      const { formValues, attendeesList, attachList } = convertFollowResponseToFormValues(follow);
      setOnsiteCustomers(attendeesList);
      setFileList(attachList);
      setFollowNo(follow.followno);
      setFollowEditComments((follow.followcomment || []).map(toFollowComment));
      // 関連フォロー情報（所属群No・紐づけ検索シード）を保持。
      // 会社名・顧客名は selectFollow レスポンス（フォロー明細）には含まれないため、
      // 表示中の名刺（cardData）を確実な供給源として優先する。
      setFollowAnchorMeta({
        groupNo: follow.relationfollowgroupno ?? null,
        companyName: cardData?.company || follow.companyname || '',
        customerName: cardData?.name || follow.customername || '',
      });
      setFollowModalMode('edit');
      setFollowFormInitialValues(formValues);
      // 編集時: 元が予定ToDoの場合のみDBのアラート値を渡す（非ToDoの場合は新規登録と同じデフォルト値を使う）
      const alertFromDb = buildAlertSettingsFromFollow(follow);
      setFollowEditAlertSettings(alertFromDb);
      // 履歴通知モーダル用: 同じ詳細データからアラート値・ToDoフラグを保持
      setHistoryNotifIsTodo(!!follow.plantodoflag);
      setHistoryNotifAlertSettings(alertFromDb);
      setShowAddFollowModal(true);
    } catch (error) {
      console.error('フォロー情報取得エラー:', error);
      message.error('フォロー情報の取得に失敗しました');
    }
  };

  /** フォロー履歴からの通知送信：通知先モーダルを開く */
  const handleOpenHistoryNotifSend = async (followno: number, assigneeUserno: number | null, isTodo: boolean) => {
    setHistoryNotifInitialRecipients(
      await buildInitialNotifRecipients(cardData?.id, assigneeUserno, user?.userno, contactUsers, isTodo)
    );
    // TODO: Teams通知実装時はコメントアウトを解除（Teamsチャネル一覧の先取得）
    // if (cardData?.id) {
    //   const channels = await followAPI.getTeamsChannels(cardData.id);
    //   setHistoryNotifTeamsChannels(channels);
    // }
    setHistoryNotifFollowNo(followno);
    setHistoryNotifModalVisible(true);
  };

  /** フォロー履歴からの通知送信確定 */
  const handleSendNotifFromHistory = async (
    recipients: { userno: number; urgent: boolean }[],
    alertSettings?: AlertSettings,
    // TODO: メールアドレス通知実装時はコメントアウトを解除
    // emailAddresses: string[],
    // TODO: Teams通知実装時はコメントアウトを解除
    // teamsChannelUrls: string[],
  ) => {
    if (!historyNotifFollowNo) return;
    try {
      // TODO: Teams・メールアドレス通知実装時は buildNoticeDestinationList(recipients, emailAddresses, teamsChannelUrls) に復元
      const noticeDestinationList = buildNoticeDestinationList(recipients);
      const alertParams = alertSettings
        ? {
            plantodoflag: true,
            prealert: alertSettings.preAlert,
            realert: alertSettings.reAlert,
            realertflag: alertSettings.reAlertFlag,
          }
        : undefined;
      const warnMsg = await followAPI.sendNotice(historyNotifFollowNo, noticeDestinationList, alertParams);
      if (warnMsg) {
        message.warning(warnMsg);
      } else {
        message.success('通知を送信しました');
      }
    } catch {
      message.error('通知の送信に失敗しました');
    }
  };

  /** フォロー履歴の通知履歴モーダルを開く */
  const handleOpenFollowNotifHistory = async (followno: number) => {
    setFollowNotifHistoryItems([]);
    setFollowNotifHistoryModalVisible(true);
    setFollowNotifHistoryLoading(true);
    try {
      const items = await followAPI.getNoticeHistory(followno);
      setFollowNotifHistoryItems(items);
    } catch {
      message.error('通知履歴の取得に失敗しました');
    } finally {
      setFollowNotifHistoryLoading(false);
    }
  };

  // キーワードを挿入する関数（textToInsert はコンポーネント側で解決済み）
  const insertKeyword = (textToInsert: string) => {
    const currentRemarks = form.getFieldValue('remarks') || '';
    const textarea = textAreaRef.current?.resizableTextArea?.textArea;
    const start = textarea?.selectionStart ?? 0;
    const end = textarea?.selectionEnd ?? 0;

    const newValue = currentRemarks.substring(0, start) + textToInsert + currentRemarks.substring(end);
    form.setFieldsValue({ remarks: newValue });

    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(start + textToInsert.length, start + textToInsert.length);
      }
    }, 0);
  };
  
  if (!cardData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh', 
        flexDirection: 'column',
        background: '#f0f2f5'
      }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>顧客データを読み込み中...</div>
      </div>
    );
  }
  
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ 
        background: '#fff', 
        padding: '0 16px', 
        display: 'flex', 
        alignItems: 'center',
        height: 'auto',
        minHeight: '64px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <Button 
          shape="circle"
          size="small"
          icon={<ArrowLeftOutlined />} 
          onClick={handleBack}
          style={{ 
            backgroundColor: 'white',
            borderColor: '#d9d9d9',
            borderWidth: '2px',
            color: '#595959',
            marginRight: '8px',
            width: '32px',
            height: '32px',
            minWidth: '32px',
            minHeight: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        />
        <div style={{ flex: 1, textAlign: 'center' }}>
          顧客詳細
        </div>
        {isEditing ? (
          <Space>
            <Button
              type="default"
              onClick={cancelEditing}
            >
              キャンセル
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={saveChanges}
              loading={isSaving}
              disabled={isSaving}
            >
              {isSaving ? '保存中...' : '保存'}
            </Button>
          </Space>
        ) : (
          <Space>
            {canEdit && (
              <Button
                type="default"
                icon={<EditOutlined />}
                onClick={startEditing}
              >
                編集
              </Button>
            )}
            {canRegisterCustomer && !isInFlutter() && (
              <Button
                type="primary"
                icon={<UserOutlined />}
                onClick={() => navigate(getRoutePath('/camera'))}
              >
                名刺取込
              </Button>
            )}
          </Space>
        )}
      </Header>

      <Content style={{
        padding: '16px',
        paddingBottom: `${16 + flutterFooterHeight}px`,
        background: '#f0f2f5'
      }}>
        {/* API エラーメッセージ */}
        {apiError && (
          <Card style={{ marginBottom: 16, backgroundColor: '#ffebee' }}>
            <div style={{ color: '#d32f2f' }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>エラー</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{apiError}</div>
            </div>
          </Card>
        )}

        {/* 基本情報カード */}
        <Card 
          style={{ 
            marginBottom: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}
        >
          {/* 名刺画像の表示（ある場合のみ） */}
          {cardData.businesscardfrontimage && (
            <div style={{
              marginBottom: '16px',
              width: '100%'
            }}>
              <div style={{
                textAlign: 'center',
                marginBottom: '8px'
              }}>
                <div style={{ display: 'inline-block', position: 'relative' }}>
                  <img
                    src={cardData.businesscardfrontimage}
                    alt="名刺表面"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      border: '1px solid #eee',
                      borderRadius: '4px',
                      display: 'block'
                    }}
                    onError={(e) => {
                      console.error('名刺画像読み込みエラー:', e);
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <button
                    style={{
                      position: 'absolute',
                      bottom: '8px',
                      right: '8px',
                      background: 'rgba(0, 0, 0, 0.45)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      padding: 0,
                      cursor: 'pointer',
                      color: '#fff',
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onClick={() => setFrontPreviewOpen(true)}
                  >
                    <ZoomInOutlined />
                  </button>
                </div>
              </div>
              {/* 裏面画像がある場合は「裏面を表示」ボタンを表示 */}
              {cardData.businesscardbackimage && (
                <div style={{ textAlign: 'right' }}>
                  <Button
                    size="small"
                    onClick={() => setShowBackSide(!showBackSide)}
                    style={{
                      backgroundColor: '#f5f5f5',
                      borderColor: '#d9d9d9',
                      color: '#595959',
                      fontSize: '12px',
                      padding: '2px 8px',
                      height: 'auto'
                    }}
                  >
                    {showBackSide ? '裏面を非表示' : '裏面を表示'}
                  </Button>
                </div>
              )}

              {/* 裏面画像の表示 */}
              {showBackSide && cardData.businesscardbackimage && (
                <div style={{
                  textAlign: 'center',
                  marginTop: '16px',
                  paddingTop: '16px',
                  borderTop: '1px solid #f0f0f0'
                }}>
                  <div style={{
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    color: '#595959',
                    fontSize: '14px'
                  }}>
                    裏面画像
                  </div>
                  <div style={{ display: 'inline-block', position: 'relative' }}>
                    <img
                      src={cardData.businesscardbackimage}
                      alt="名刺裏面"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        objectFit: 'contain',
                        border: '1px solid #eee',
                        borderRadius: '4px',
                        display: 'block'
                      }}
                      onError={(e) => {
                        console.error('名刺裏面画像読み込みエラー:', e);
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <button
                      style={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        background: 'rgba(0, 0, 0, 0.45)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        cursor: 'pointer',
                        color: '#fff',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      onClick={() => setBackPreviewOpen(true)}
                    >
                      <ZoomInOutlined />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            {/* ランク表示を削除 */}
            <Title level={4} style={{ margin: 0 }}>
              {cardData.name}
              {(() => {
                // 拡張フィールドAPIでfuriganaフィールドが存在する場合のみ表示
                if (!furiganaField) {
                  return null;
                }

                // optionfieldからfuriganaを取得
                let furiganaValue = '';
                if (cardData.optionfield) {
                  try {
                    const parsedOptionField = JSON.parse(cardData.optionfield);
                    if (parsedOptionField.furigana) {
                      furiganaValue = parsedOptionField.furigana;
                    }
                  } catch (error) {
                    console.error('optionfield解析エラー:', error);
                  }
                }

                // furiganaがあれば（）書きで表示
                if (furiganaValue) {
                  return (
                    <Text type="secondary" style={{ fontSize: '14px', marginLeft: '8px', fontWeight: 'normal' }}>
                      （{furiganaValue}）
                    </Text>
                  );
                }

                // 既存のkana表示（互換性のため残す）
                if (cardData.kana) {
                  return (
                    <Text type="secondary" style={{ fontSize: '14px', marginLeft: '8px', fontWeight: 'normal' }}>
                      （{cardData.kana}）
                    </Text>
                  );
                }

                return null;
              })()}
            </Title>
          </div>
          
          <Descriptions 
            bordered 
            size="small" 
            column={{ xs: 1, sm: 2, md: 3 }}
            labelStyle={{ width: '100px' }}
          >
            <Descriptions.Item label="会社名" span={3}>{cardData.company}</Descriptions.Item>
            <Descriptions.Item label="部署">{cardData.department || '-'}</Descriptions.Item>
            <Descriptions.Item label="役職">{cardData.position || '-'}</Descriptions.Item>
            <Descriptions.Item label="ランク">
              <Tag color={(() => {
                // 一覧画面と同じロジックを使用してランク色を取得
                if (cardData?.id) {
                  // ranknoの値を取得（APIレスポンスから）
                  const rankno = cardData.rankno !== undefined ? cardData.rankno : -1;
                  // ranknoに基づいて色を取得
                  return getRankColorByIndex(rankno);
                }
                return currentRankColor; // バックアップとして現在の色を使用
              })()}>
                {cardData.rank || '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="担当" span={2}>
              {renderContactUsers()}
            </Descriptions.Item>
          </Descriptions>
          
          <Space direction="vertical" size={8} style={{ width: '100%', marginTop: '16px' }}>
            {cardData.phone && (
              <div style={{ display: 'flex' }}>
                <PhoneOutlined style={{ marginRight: '8px', marginTop: '4px', color: '#0693e3' }} />
                <div>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>電話番号</div>
                  <div><Linkify text={cardData.phone} /></div>
                </div>
              </div>
            )}
            
            {cardData.mobile && (
              <div style={{ display: 'flex' }}>
                <PhoneOutlined style={{ marginRight: '8px', marginTop: '4px', color: '#0693e3' }} />
                <div>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>携帯電話</div>
                  <div><Linkify text={cardData.mobile} /></div>
                </div>
              </div>
            )}
            
            {/* E-mail：配信NGバッジを右上に */}
            {cardData.email && (
              <div style={{ display: 'flex' }}>
                <MailOutlined style={{ marginRight: '8px', marginTop: '4px', color: '#0693e3' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#8c8c8c',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <span>E-mail</span>
                    {cardData.doNotDistribute && (
                      <Tag 
                        color="error" 
                        style={{ 
                          fontSize: '10px',
                          padding: '0 6px',
                          lineHeight: '18px',
                          marginLeft: '6px',
                          marginBottom: 0
                        }}
                      >
                        配信NG
                      </Tag>
                    )}
                  </div>
                  <div><Linkify text={cardData.email} /></div>
                </div>
              </div>
            )}
            
            {cardData.address && (
              <div style={{ display: 'flex' }}>
                <HomeOutlined style={{ marginRight: '8px', marginTop: '4px', color: '#0693e3' }} />
                <div>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>住所</div>
                  <div><Linkify text={cardData.address} /></div>
                </div>
              </div>
            )}

            {cardData.url && (
              <div style={{ display: 'flex' }}>
                <LinkOutlined style={{ marginRight: '8px', marginTop: '4px', color: '#0693e3' }} />
                <div>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>URL</div>
                  <div><Linkify text={cardData.url} /></div>
                </div>
              </div>
            )}
          </Space>
        </Card>
        
        {/* タブエリア */}
        <Card ref={followHistoryTabRef} style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <Tabs 
            activeKey={activeTab} 
            onChange={handleTabChange}
            tabBarExtraContent={
              <Button 
                type="primary" 
                size="small"
                onClick={() => {
                  // 直前の編集で残った followNo/添付/同席者/アンカーを新規モードに持ち込まないよう必ずリセット
                  resetFollowFormState();
                  setFollowFormInitialValues(undefined);
                  setShowAddFollowModal(true);
                }}
                style={{
                  fontSize: '12px',
                  height: '28px',
                  padding: '0 8px'
                }}
              >
                フォロー追加
              </Button>
            }
          >
            {/* 詳細情報タブ */}
            <TabPane tab="詳細情報" key="1">
              <div id="detail-info-section">
                {isEditing ? (
                  <Form
                    form={form}
                    layout="vertical"
                    onValuesChange={handleFormValuesChange}
                  >
                  <Row gutter={16}>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item
                        name="name"
                        label={<span>顧客名<span style={{ color: 'red' }}> *</span></span>}
                        validateStatus={formValidity.name && !formValidity.name.valid ? 'error' : ''}
                        help={formValidity.name && !formValidity.name.valid ? formValidity.name.message : ''}
                      >
                        <Input
                          onBlur={handleNameBlur}
                          onCompositionStart={() => {
                            // 現在のフリガナ値を保存して追記モードにする
                            autoKanaHandlerRef.current.handleCompositionStart(furiganaValue);
                          }}
                          onCompositionUpdate={(e) => {
                            // ひらがなのみの場合のみカタカナに変換してフリガナに設定
                            if (isHiraganaOnly(e.data || '')) {
                              const kana = autoKanaHandlerRef.current.handleCompositionUpdate(e);
                              if (furiganaField) {
                                setFuriganaValue(kana);
                              }
                            }
                          }}
                          onCompositionEnd={() => {
                            // 変換確定時はフリガナを更新しない（変換中のひらがなカタカナをキープ）
                            autoKanaHandlerRef.current.reset(true); // previousValueは保持
                          }}
                        />
                      </Form.Item>
                    </Col>
                    {furiganaField && (
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item label={furiganaField.dispname.replace(/[（(][^）)]*[）)]/g, '').trim()}>
                          <Input
                            value={furiganaValue}
                            onChange={(e) => setFuriganaValue(e.target.value)}
                            onBlur={(e) => {
                              // フォーカスを外した時にひらがなをカタカナに変換
                              const convertedValue = hiraganaToKatakana(e.target.value);
                              setFuriganaValue(convertedValue);
                            }}
                            placeholder={`${furiganaField.dispname.replace(/[（(][^）)]*[）)]/g, '').trim()}を入力`}
                          />
                        </Form.Item>
                      </Col>
                    )}
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item name="rank" label="ランク">
                        <Select placeholder="ランクを選択">
                          {rankOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  
                  <Row gutter={16}>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item name="company" label="会社名">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item name="department" label="部署">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item name="position" label="役職">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  
                  <Row gutter={16}>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item
                        name="phone"
                        label={<span>電話番号<span style={{ color: 'red' }}> *</span></span>}
                        validateStatus={formValidity.phone && !formValidity.phone.valid ? 'error' : ''}
                        help={formValidity.phone && !formValidity.phone.valid ? formValidity.phone.message : ''}
                      >
                        <Input onBlur={handlePhoneBlur} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item name="mobile" label="携帯電話">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item name="fax" label="FAX">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="email"
                        label={<span>E-mail<span style={{ color: 'red' }}> *</span></span>}
                        validateStatus={formValidity.email && !formValidity.email.valid ? 'error' : ''}
                        help={formValidity.email && !formValidity.email.valid ? formValidity.email.message : ''}
                      >
                        <Input onBlur={handleEmailBlur} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="doNotDistribute" label="配信NG">
                        <Select placeholder="配信設定を選択">
                          <Option value={false}>OK</Option>
                          <Option value={true}>NG</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="url" label="URL">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item name="address" label="住所">
                    <TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
                  </Form.Item>

                  <Form.Item name="country" label="国">
                    <Input placeholder="国名を入力" />
                  </Form.Item>

                  <Form.Item 
                    name="remarks" 
                    label={
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        minHeight: '32px'
                      }}>
                        <span style={{ 
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                          marginRight: '16px'
                        }}>備考</span>
                        <Space size="middle" style={{ flexShrink: 0 }}>
                          <Button 
                            type="primary" 
                            size="small" 
                            icon={<TagOutlined />}
                            onClick={(e) => {
                              e.preventDefault();
                              setKeywordModalVisible(true);
                            }}
                          >
                            キーワード入力
                          </Button>
                          <Button 
                            type="primary" 
                            size="small" 
                            icon={<RobotOutlined />}
                            onClick={(e) => {
                              e.preventDefault();
                              setSelectedAssistEnq(defaultCustomerAssistNo);
                              setAssistModalError('');
                              setAssistModalVisible(true);
                              
                              // Add mobile keyboard handling
                              if (window.innerWidth <= 768) {
                                // Save current scroll position
                                const originalScrollY = window.scrollY;
                                
                                const setupKeyboardHandling = () => {
                                  const modal = document.querySelector('.ant-modal');
                                  if (!modal) return;
                                  
                                  const handleInputFocus = (input: Element) => {
                                    const modalElement = modal as HTMLElement;
                                    modalElement.style.position = 'fixed !important';
                                    modalElement.style.top = '0px !important';
                                    modalElement.style.left = '0 !important';
                                    modalElement.style.right = '0 !important';
                                    modalElement.style.margin = '0 auto !important';
                                    modalElement.style.transform = 'none !important';
                                    modalElement.style.zIndex = '9999 !important';
                                    modalElement.style.width = 'auto !important';
                                    modalElement.style.maxWidth = '90vw !important';
                                    
                                    // Don't scroll page - keep original position
                                    // window.scrollTo(0, 0);
                                    
                                    // Find assist input content area and scroll input to top
                                    setTimeout(() => {
                                      const modalBody = modal.querySelector('.ant-modal-body') as HTMLElement;
                                      const aptoenqform = modal.querySelector('#aptoenqform') as HTMLElement;
                                      
                                      if (modalBody && aptoenqform && input instanceof HTMLElement) {
                                        // Force reflow to ensure correct positioning
                                        void modalBody.offsetHeight;
                                        
                                        // Try multiple methods to ensure input is visible
                                        // Method 1: scrollIntoView on the input element
                                        try {
                                          input.scrollIntoView({
                                            behavior: 'smooth',
                                            block: 'start',
                                            inline: 'nearest'
                                          });
                                        } catch (e) {
                                          // Fallback: manual scroll calculation
                                          const inputTop = input.offsetTop || 0;
                                          const targetScrollTop = Math.max(0, inputTop - 50);
                                          
                                          modalBody.scrollTo({
                                            top: targetScrollTop,
                                            behavior: 'smooth'
                                          });
                                        }
                                      }
                                    }, 300);
                                  };
                                  
                                  const addFocusListeners = () => {
                                    // Target only inputs within the assist content area
                                    const inputs = modal.querySelectorAll('#aptoenqform input, #aptoenqform textarea, .ant-modal-body input, .ant-modal-body textarea');
                                    inputs.forEach(input => {
                                      input.removeEventListener('focus', () => handleInputFocus(input));
                                      input.addEventListener('focus', () => handleInputFocus(input));
                                    });
                                  };
                                  
                                  addFocusListeners();
                                  
                                  const observer = new MutationObserver(() => {
                                    setTimeout(addFocusListeners, 300);
                                  });
                                  
                                  observer.observe(modal, {
                                    childList: true,
                                    subtree: true
                                  });
                                  
                                  // Store original scroll position for restoration
                                  (modal as any)._originalScrollY = originalScrollY;
                                };
                                
                                setTimeout(setupKeyboardHandling, 1000);
                                setTimeout(setupKeyboardHandling, 2000);
                              }
                            }}
                          >
                            アシスト入力
                          </Button>
                        </Space>
                      </div>
                    }
                  >
                    <TextArea autoSize={{ minRows: 3, maxRows: 6 }} ref={textAreaRef} />
                  </Form.Item>
                  
                  {/* 拡張フィールド表示（編集モード） */}
                  {optionFields?.customeroptionfieldlist && optionFields.customeroptionfieldlist.length > 0 && (
                    <div className="option-field-group" style={{ marginTop: '24px' }}>
                      <OptionFieldGroup
                        title=""
                        fields={optionFields.customeroptionfieldlist.filter(field => field.optionfieldkey !== 'furigana')}
                        values={optionFieldValues}
                        onFieldChange={handleOptionFieldChange}
                        prefix="customer"
                        readonly={false}
                      />
                    </div>
                  )}
                  
                  {/* 編集モード用のボタン */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    gap: '8px', 
                    marginTop: '24px',
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <Button 
                      onClick={cancelEditing}
                      size="middle"
                    >
                      キャンセル
                    </Button>
                    {canDeleteCustomer && (
                      <Button
                        type="primary"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => setCustomerDeleteConfirmVisible(true)}
                        size="middle"
                        loading={isDeleting}
                        disabled={isDeleting}
                      >
                        削除
                      </Button>
                    )}
                    <Button 
                      type="primary" 
                      icon={<SaveOutlined />}
                      onClick={saveChanges}
                      size="middle"
                      loading={false}
                    >
                      保存
                    </Button>
                  </div>
                </Form>
              ) : (
                <>
                  {cardData.remarks && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <FileTextOutlined style={{ marginRight: '8px', color: '#0693e3' }} />
                        <Text strong>備考</Text>
                      </div>
                      <Paragraph style={{ 
                        padding: '12px 16px', 
                        background: '#f5f9fd', 
                        borderRadius: '4px',
                        whiteSpace: 'pre-line'
                      }}>
                        <Linkify text={cardData.remarks} />
                      </Paragraph>
                    </div>
                  )}

                  {/* 拡張フィールド表示（表示モード） */}
                  {optionFields?.customeroptionfieldlist && optionFields.customeroptionfieldlist.length > 0 && (
                    <div className="option-field-group" style={{ marginTop: '16px' }}>
                      <OptionFieldGroup
                        title=""
                        fields={optionFields.customeroptionfieldlist.filter(field => field.optionfieldkey !== 'furigana')}
                        values={optionFieldValues}
                        onFieldChange={handleOptionFieldChange}
                        prefix="customer"
                        readonly={true}
                      />
                    </div>
                  )}
                  
                </>
              )}
              </div>
            </TabPane>
            
            {/* フォロー履歴タブ */}
            <TabPane tab="フォロー履歴" key="2">
              <FollowHistoryPanel
                followHistory={cardData.followHistory || []}
                isLoading={isLoadingFollowHistory}
                user={user}
                onAttachmentLoad={handleFollowAttachmentLoad}
                onAttachmentViewClick={handleFollowAttachmentViewClick}
                onDeleteClick={(id) => {
                  setDeleteFollowTargetId(id);
                  setDeleteFollowModalVisible(true);
                }}
                onEditClick={handleEditFollowItem}
              />
            </TabPane>
          </Tabs>
        </Card>
        
        {/* フォロー削除確認モーダル */}
        <Modal
          title="フォローの削除"
          open={deleteFollowModalVisible}
          onCancel={() => {
            setDeleteFollowModalVisible(false);
            setDeleteFollowTargetId(null);
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setDeleteFollowModalVisible(false);
                setDeleteFollowTargetId(null);
              }}
            >
              キャンセル
            </Button>,
            <Button
              key="delete"
              danger
              type="primary"
              onClick={async () => {
                if (!deleteFollowTargetId) return;
                try {
                  await followAPI.deleteFollow(deleteFollowTargetId);
                  message.success('フォローを削除しました');
                  setDeleteFollowModalVisible(false);
                  setDeleteFollowTargetId(null);
                  if (cardData?.id) {
                    await fetchFollowHistory(cardData.id);
                  }
                } catch (error) {
                  console.error('フォロー削除エラー:', error);
                  message.error('フォローの削除に失敗しました');
                }
              }}
            >
              削除
            </Button>,
          ]}
          centered
        >
          <p>このフォローを削除してもよろしいですか？</p>
        </Modal>

        {/* フォロー追加/更新モーダル */}
        <FollowFormModal
          open={showAddFollowModal}
          mode={followModalMode}
          title={followModalMode === 'edit' ? 'フォロー更新' : 'フォロー追加'}
          submitLabel={followModalMode === 'edit' ? '更新' : '追加'}
          onCancel={closeFollowModal}
          onSubmit={handleFollowFormSubmit}
          contactUsers={contactUsers}
          isLoadingContactUsers={isLoadingContactUsers}
          initialValues={followFormInitialValues}
          defaultAssigneeUserno={user?.userno ?? null}
          zIndex={1000}
          flutterFooterHeight={flutterFooterHeight}
          onAssistClick={followOnAssistClick}
          onVoiceClick={followOnVoiceClick}
          controlledOnsiteCustomers={onsiteCustomers}
          onOnsiteCustomerRemove={customerno => setOnsiteCustomers(prev => prev.filter(c => c.customerno !== customerno))}
          onCustomerSelectOpen={() => setCustomerSelectVisible(true)}
          onOnsiteCustomersClear={() => setOnsiteCustomers([])}
          controlledFileList={fileList}
          onFileAdd={followOnFileAdd}
          onFilePreview={file => handleFilePreview(file)}
          onFileDownload={file => handleFileDownload(file)}
          onFileDelete={(_file, index) => {
            setDeleteFileTargetIndex(index);
            setDeleteFileConfirmVisible(true);
          }}
          onContentSetterReady={followOnContentSetterReady}
          onNotifSendClick={followNo ? (assigneeUserno, isTodo) => handleOpenHistoryNotifSend(followNo, assigneeUserno, isTodo) : undefined}
          onNotifHistoryClick={followNo ? () => handleOpenFollowNotifHistory(followNo) : undefined}
          onShowRelatedFollow={followNo && relatedFollow.hasRelation ? () => { setShowAddFollowModal(false); relatedFollow.openRelatedFollow(); } : undefined}
          onShowLinkFollow={followNo ? () => { setShowAddFollowModal(false); relatedFollow.openLinkFollow(); } : undefined}
          comments={followNo ? followComments.comments : undefined}
          onAddComment={followNo ? followComments.openAdd : undefined}
          onEditComment={followNo ? followComments.openEdit : undefined}
          onDeleteComment={followNo ? followComments.requestDelete : undefined}
          currentUserNo={user?.userno ?? null}
        />

        {/* 関連フォロー（紐づけ選択・関連フォロー表示）モーダル */}
        {relatedFollow.renderModals()}

        {/* フォローコメント（追加・編集・削除・通知先選択）モーダル */}
        {followComments.renderModals()}


        {/* 顧客選択パネル（同席者追加） */}
        <CustomerSelectPanel
          visible={customerSelectVisible}
          onClose={() => setCustomerSelectVisible(false)}
          defaultCompany={cardData?.company}
          flutterFooterHeight={flutterFooterHeight}
          onSelectionChange={customers => setOnsiteCustomers(customers.map(c => ({
            customerno: c.customerno,
            customername: c.customername,
            companyname: c.companyname || '',
            deptname: c.deptname || '',
          })))}
          selectedCustomersFromParent={onsiteCustomers as Customer[]}
          zIndex={1100}
          excludeCustomerNo={cardData?.id}
        />

        {followRenderModals()}

        {/* キーワード入力モーダル */}
        <KeywordSelectModal
          open={keywordModalVisible}
          options={keywordOptions}
          error={keywordLoadError}
          loading={isLoadingKeywords}
          onClose={() => setKeywordModalVisible(false)}
          onSelect={insertKeyword}
          maskStyle={{ overflow: 'hidden' }}
          previewRows={4}
          flutterFooterHeight={flutterFooterHeight}
        />
        
        {/* アシスト入力モーダル */}
        <AssistInputModal
          open={assistModalVisible}
          onClose={() => {
            setAssistModalVisible(false);
            setSelectedAssistEnq(null);
          }}
          options={assistOptions}
          optionsError={assistOptionsError}
          selectedValue={selectedAssistEnq}
          onValueChange={handleAssistOptionChange}
          contentHtml={assistContentHtml}
          loading={loadingAssistContent}
          error={assistModalError}
          errorRef={assistModalErrorRef}
          contentRef={assistContentRef}
          hiddenInputId="selectEnqueteInputAid_followContent"
          width={700}
          wrapClassName="selectEnqueteInputAid businesscard-assist-modal"
          bodyStyle={{ padding: 0 }}
          layout="flex"
        />
        
        {/* 名刺表面 拡大プレビュー */}
        <BusinessCardImageModal
          open={frontPreviewOpen}
          src={cardData?.businesscardfrontimage}
          alt="名刺表面"
          flutterFooterHeight={flutterFooterHeight}
          onClose={() => setFrontPreviewOpen(false)}
        />
        {/* 名刺裏面 拡大プレビュー */}
        <BusinessCardImageModal
          open={backPreviewOpen}
          src={cardData?.businesscardbackimage}
          alt="名刺裏面"
          flutterFooterHeight={flutterFooterHeight}
          onClose={() => setBackPreviewOpen(false)}
        />

        {/* 顧客削除前の確認モーダル */}
        <Modal
          centered
          open={customerDeleteConfirmVisible}
          title="確認"
          okText="はい"
          cancelText="いいえ"
          onOk={() => {
            // モーダルを閉じる
            setCustomerDeleteConfirmVisible(false);
            deleteChanges();
          }}
          onCancel={() => {
            // キャンセル時はモーダルを閉じるだけ
            setCustomerDeleteConfirmVisible(false);
          }}
        >
          <p>削除します。よろしいですか？</p>
        </Modal>

        {/* ファイル追加前の確認モーダル（mask={false}） */}
        <Modal
          centered
          open={fileAddConfirmVisible}
          title="確認"
          okText="はい"
          cancelText="いいえ"
          zIndex={1010}
          mask={false}
          onOk={async () => {
            // FollowFormModal でバリデーション済みなので担当者チェック不要
            // モーダルを閉じる
            setFileAddConfirmVisible(false);
            
            // 処理中モーダルを表示
            setProcessingModalVisible(true);
            
            try {
              // フォロー情報を登録
              const followId = await registerFollowData(true);
              
              if (followId) {
                // APIから正しくフォローNoが取得できた場合
                setProcessingModalVisible(false);
                
                // 更新か新規登録かによってメッセージを変える
                message.success(followModalMode === 'edit' ? 'フォロー情報を更新しました' : 'フォロー情報を保存しました');
                
                setFollowNo(followId);
                followOpenFileUploadModal(followId);
              } else {
                // 何らかの理由でフォローNoが取得できなかった場合
                setProcessingModalVisible(false);
                message.error('フォロー情報の保存に失敗しました');
              }
            } catch (error) {
              console.error('フォロー登録エラー:', error);
              setProcessingModalVisible(false);
              message.error('フォロー情報の保存中にエラーが発生しました');
            }
          }}
          onCancel={() => {
            // キャンセル時はモーダルを閉じるだけ
            setFileAddConfirmVisible(false);
          }}
        >
          <p>現在の状態を保存します。よろしいですか？</p>
        </Modal>

        {/* フォロー保存中モーダル */}
        <Modal
          open={processingModalVisible}
          footer={null}
          closable={false}
          centered
          zIndex={1020}
          width={200}
        >
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>処理中...</p>
          </div>
        </Modal>

        {/* 添付ファイル削除確認モーダル（mask={false}） */}
        <Modal
          title="ファイルの削除"
          open={deleteFileConfirmVisible}
          okText="削除"
          okType="danger"
          cancelText="キャンセル"
          zIndex={1010}
          mask={false}
          centered
          onOk={async () => {
            if (deleteFileTargetIndex !== null) {
              const targetFile = fileList[deleteFileTargetIndex];

              // API にアップロード済みのファイルは削除APIを呼ぶ
              if (targetFile?.attachfileno) {
                try {
                  const companyId = await indexedDBStorage.getItem('companyId') || '';
                  await followAPI.deleteAttachment({
                    attachfileno: targetFile.attachfileno,
                    companyid: companyId,
                  });
                } catch (error) {
                  message.error('ファイルの削除に失敗しました');
                  setDeleteFileConfirmVisible(false);
                  setDeleteFileTargetIndex(null);
                  return;
                }
              }

              const newFileList = fileList.filter((_, i) => i !== deleteFileTargetIndex);
              const newAttachFiles = attachFiles.filter((_, i) => i !== deleteFileTargetIndex);
              setFileList(newFileList);
              setAttachFiles(newAttachFiles);
            }
            setDeleteFileConfirmVisible(false);
            setDeleteFileTargetIndex(null);
          }}
          onCancel={() => {
            setDeleteFileConfirmVisible(false);
            setDeleteFileTargetIndex(null);
          }}
        >
          <p>「{deleteFileTargetIndex !== null ? fileList[deleteFileTargetIndex]?.name : ''}」を削除しますか？</p>
        </Modal>

        {/* フォロー履歴 添付ファイルモーダル */}
        <FollowAttachmentModal
          open={historyAttachModalVisible}
          files={historyAttachModalFiles}
          onClose={() => setHistoryAttachModalVisible(false)}
          onFilesChange={setHistoryAttachModalFiles}
          onFileDeleted={(attachfileno) => {
            if (historyAttachModalFollowIndex !== null && cardData) {
              const updatedHistory = cardData.followHistory?.map((h, i) => {
                if (i === historyAttachModalFollowIndex) {
                  const newAttachments = h.attachments?.filter(a => a.attachfileno !== attachfileno);
                  return { ...h, attachments: newAttachments, hasAttachment: (newAttachments?.length ?? 0) > 0 };
                }
                return h;
              });
              setCardData(prev => { if (!prev) return prev; return { ...prev, followHistory: updatedHistory }; });
            }
          }}
          onFilePreview={handleFilePreview}
          onFileDownload={handleFileDownload}
          zIndex={1010}
          confirmZIndex={1020}
          scrollable
        />

        {/* フォロー登録の確認モーダル（FollowFormModal の子 → mask={false} はデフォルト） */}
        <NotifRecipientModal
          open={followNotifModalVisible}
          onCancel={() => setFollowNotifModalVisible(false)}
          // TODO: Teams・メールアドレス通知実装時は handleAddFollow(recipients, teamsChannelUrls, emailAddresses, alertSettings) に復元
          onSubmit={(recipients, _teams, _emails, alertSettings) => {
            setFollowNotifModalVisible(false);
            handleAddFollow(recipients, alertSettings);
          }}
          contactUsers={contactUsers}
          initialRecipients={followNotifInitialRecipients}
          // TODO: Teams通知実装時はコメントアウトを解除
          // teamsChannels={followNotifTeamsChannels}
          title="フォロー登録の確認"
          submitLabel="登録"
          zIndex={1010}
          showAlertSettings={followNotifIsTodo}
          initialAlertSettings={followModalMode === 'edit' ? followEditAlertSettings : undefined}
        />

        {/* フォロー履歴からの通知送信モーダル（mask={false} はデフォルト） */}
        <NotifRecipientModal
          open={historyNotifModalVisible}
          onCancel={() => setHistoryNotifModalVisible(false)}
          // TODO: Teams・メールアドレス通知実装時は handleSendNotifFromHistory(recipients, alertSettings, teamsChannelUrls, emailAddresses) に復元
          onSubmit={(recipients, _teams, _emails, alertSettings) => {
            setHistoryNotifModalVisible(false);
            handleSendNotifFromHistory(recipients, alertSettings);
          }}
          contactUsers={contactUsers}
          initialRecipients={historyNotifInitialRecipients}
          // TODO: Teams通知実装時はコメントアウトを解除
          // teamsChannels={historyNotifTeamsChannels}
          title="通知"
          submitLabel="送信"
          showConfirm
          zIndex={1010}
          showAlertSettings={historyNotifIsTodo}
          initialAlertSettings={historyNotifAlertSettings}
        />

        {/* フォロー通知履歴モーダル（mask={false}） */}
        <Modal
          title="フォロー通知履歴"
          open={followNotifHistoryModalVisible}
          onCancel={() => setFollowNotifHistoryModalVisible(false)}
          centered
          width={480}
          mask={false}
          wrapStyle={flutterFooterHeight > 0 ? { paddingBottom: `${flutterFooterHeight}px`, boxSizing: 'border-box' } : undefined}
          styles={flutterFooterHeight > 0 ? { content: { maxHeight: `calc(100dvh - ${flutterFooterHeight}px - 40px)`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }, body: { flex: 1, overflowY: 'auto', minHeight: 0 } } : undefined}
          zIndex={1010}
          footer={<Button onClick={() => setFollowNotifHistoryModalVisible(false)}>閉じる</Button>}
        >
          {followNotifHistoryLoading ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Spin />
            </div>
          ) : (
            <div style={{ border: '1px solid #f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '6px 10px', background: '#fafafa', borderBottom: '1px solid #f0f0f0', fontSize: 13, fontWeight: 'bold', color: 'rgba(0,0,0,0.65)' }}>
                <div>通知日時</div>
                <div>部署</div>
                <div>名前</div>
              </div>
              {followNotifHistoryItems.length === 0 ? (
                <div style={{ padding: '16px 10px', textAlign: 'center', color: '#8c8c8c', fontSize: 13 }}>
                  通知履歴はありません
                </div>
              ) : (
                followNotifHistoryItems.map((h, i) => (
                  <div
                    key={h.noticeunitno ?? i}
                    style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '7px 10px', borderBottom: '1px solid #f5f5f5', alignItems: 'center', backgroundColor: h.urgentflag ? '#fff1f0' : undefined }}
                  >
                    <div style={{ fontSize: 12, color: '#595959' }}>{(h.noticedatetime || h.noticedate) ? dayjs(h.noticedatetime || h.noticedate).format('YYYY-MM-DD HH:mm') : ''}</div>
                    <div style={{ fontSize: 13 }}>{h.deptname || ''}</div>
                    <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {h.urgentflag && (
                        <span style={{ fontSize: 10, color: '#fa541c', fontWeight: 'bold', border: '1px solid #fa541c', borderRadius: 2, padding: '0 3px', lineHeight: '16px' }}>至急</span>
                      )}
                      {h.username || h.name || ''}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </Modal>
      </Content>
    </Layout>
  );
};

export default BusinessCardDetailPage;